import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import PauseIcon from '@mui/icons-material/Pause';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import TollIcon from '@mui/icons-material/Toll';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Alert, Box, CircularProgress, Divider, IconButton, Popover, Tooltip, Typography, keyframes, useMediaQuery, useTheme } from "@mui/material";
import { purple } from '@mui/material/colors';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useConfetti } from "../../../context/ConfettiContext";
import PuzzleAccessGate from "../../../pages/login/PuzzleAccessGate";
import { trackEvent } from "../../../services/GoogleAnalytics";
import AuthService from "../../../services/AuthService";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import HomeCreditService from "../../../services/HomeCreditService";
import HomePuzzleCoinsService from "../../../services/HomePuzzleCoinsService";
import { resolveTotalPuzzleCoins } from "../../../services/PuzzleCoinsUtil";
import AudioButton from "../../components/AudioButton";
import { PUZZLE_BGM_TRACKS } from "../../components/bgmTracks";
import CustomSnackbar from "../../components/CustomSnackbar";
import LoginConfirmDialog from "../../components/LoginConfirmDialog";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import WordNetworkPanel from "../../components/puzzle/WordNetworkPanel";
import ManualValidationInput from "../common/components/ManualValidationInput";
import PlayElapsedTimer from "../common/components/PlayElapsedTimer";
// import GamePausedOverlay from "../common/components/GamePausedOverlay";
import usePlayVisibilityPause from "../common/hooks/usePlayVisibilityPause";
import SocialShareDropdown from "../common/components/SocialShareDropdown";
import FeedbackDialog from "../common/feedback/FeedbackDialog";
import FeedbackService from "../common/feedback/FeedbackService";
import HomeService from "../../../services/HomeService";
import {
    calculateProgress,
    findMatchingCellsAsTyped,
    findMatchingWord,
    findWordInGrid,
    isWordAlreadyFound,
    normalizeWord,
    parseWordsFound,
    validateWordPath
} from "../common/utils/PuzzlePlayLogic";
import { getLocalizedStoryFromTemplate } from "../../../admin/pzl_common/getEnStoryFromTemplate";
import PdfFormatDialog, {
    getPdfDownloadUseDisplayClue,
    PDF_FORMAT_DEFAULT_KEY,
} from "../../../admin/pzl_common/PdfFormatDialog";
import { UserLayoutContext } from "../../route/UserLayoutContext";
import CopyrightLine from "../common/CopyrightLine";
import SuccessCard from "../common/components/SuccessCard";
import WordsToFindDrawer from "./components/WordsToFindDrawer";
import GridPlayProgressAccordions from "./components/GridPlayProgressAccordions";
import GridGrid from "./GridGrid";
import GridService from "./GridService";
import SlidingPlayMistBackground from "../sliding/SlidingPlayMistBackground";
import { ACCENT, SITE_BG, SURFACE } from "../../../dark-theme/palette";
import { panelSurfaceSx, playPillButtonSx } from "../../../dark-theme/surfaces";
import WordRevealFx, {
    WORD_REVEAL_BANK_BOUNCE_MS,
    WORD_REVEAL_ILLUMINATE_MS,
    WORD_REVEAL_LETTER_STAGGER_MS,
    measureWordBankRect,
    measureWordCellRects,
} from "../common/components/WordRevealFx";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import Logger from "../../../services/Logger";
import {
    buildPuzzleAccessContext,
    canAccessWordPuzzle,
    isCatalogFreePuzzle,
    isWordPuzzleCreditGated,
    isWordPuzzleOpenAccess,
} from "../common/puzzleTierAccess";
import { usePagesTranslation } from "../../locales";
import {
    buildPuzzleWordMatchSet,
    buildStoryTokenToClueNumberMap,
} from "../../story/storyPlayUtils";
import StoryPlayStoryAccordion from "../../story/StoryPlayStoryAccordion";
import StoryPlayStoryDialog from "../../story/StoryPlayStoryDialog";
import {
    collectWordObjsForStoryMask,
    MaskedStorySnippet,
} from "../../browse-stories/storyCardMask";
import { resolveStoryShareIdFromPlayPayload } from "../../browse-stories/storyRoUtils";
import { GRID_HINT_WORD_LIMIT } from "../common/crosswordHintWordLimit";
import { usePageTitle } from "../common/hooks/usePageTitle";
import usePlayRecord from "../common/hooks/usePlayRecord";
import {
    getGridHintTargetWordsNormalized,
    gridCoordGetFirstNPositions,
    hintRevealSlotCount,
    hintUsedStringFromPlayRo,
    hintUsedStringWithPendingAppends,
    mergePuzzleHintUsedIntoState,
} from "../common/utils/playHintTargets";

/** Template + puzzle root may both carry story; prefer template, else merge for localized title/body. */
function gridStorySourceRecord(puzzleData) {
    if (!puzzleData) return null;
    const t = puzzleData.template;
    if (t && (t.story != null || t.storyJson != null)) return t;
    if (puzzleData.story != null || puzzleData.storyJson != null) {
        return {
            ...(t || {}),
            language: puzzleData.language ?? t?.language,
            story: puzzleData.story ?? t?.story,
            storyJson: puzzleData.storyJson ?? t?.storyJson,
        };
    }
    return t || null;
}

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toUpperCase();
};

/** Classic / Mini board dims. Prefer template gridRows×gridCols; HIDDEN_MESSAGE is always 12×12. */
function resolveGridDims(puzzle) {
    if (!puzzle) return { rows: 12, cols: 12 };
    const type = puzzle.type || puzzle.template?.type;
    if (type === "HIDDEN_MESSAGE") return { rows: 12, cols: 12 };

    const tpl = puzzle.template || {};
    const rows = Number(tpl.gridRows ?? tpl.grid_rows ?? puzzle.gridRows ?? puzzle.grid_rows);
    const cols = Number(tpl.gridCols ?? tpl.grid_cols ?? puzzle.gridCols ?? puzzle.grid_cols);
    if (
        Number.isFinite(rows) &&
        rows >= 5 &&
        rows <= 12 &&
        Number.isFinite(cols) &&
        cols >= 5 &&
        cols <= 12
    ) {
        return { rows, cols };
    }

    const lettersSources = [puzzle.letters, puzzle.details?.lettersGrid];
    for (const src of lettersSources) {
        if (typeof src !== "string" || !src) continue;
        const lines = src.split("\n").map((l) => l.replace(/\r$/, ""));
        while (lines.length > 0 && lines[lines.length - 1].trim() === "") lines.pop();
        const r = lines.length;
        const c = Math.max(0, ...lines.map((l) => l.length));
        if (r >= 5 && r <= 12 && c >= 5 && c <= 12) {
            return { rows: r, cols: c };
        }
    }
    return { rows: 12, cols: 12 };
}

/** Column count for CSS grid (same as rows on square boards). */
function resolveGridSize(puzzle) {
    return resolveGridDims(puzzle).cols;
}

/** Build rows×cols uppercase grid from a newline-separated letters string (pad/truncate). */
function buildGridFromLetters(letters, rows, cols, language) {
    const lines = letters.split("\n");
    const paddedLines = lines.slice(0, rows);
    while (paddedLines.length < rows) {
        paddedLines.push("");
    }

    return paddedLines.map((line) => {
        const chars = line.split("").map((char) => {
            // Normalize French characters (remove accents) - function already converts to uppercase
            if (language === "french") {
                return normalizeFrenchWord(char);
            }
            return char.toUpperCase();
        });
        while (chars.length < cols) {
            chars.push(" ");
        }
        return chars.slice(0, cols);
    });
}

/** Raw puzzle (API or decrypted how-to JSON) → grid + word list. Matches `loadPuzzle` processing. */
function buildProcessedGridFieldsFromRawPuzzle(puzzle) {
    const wordsArray = puzzle.words ? puzzle.words.split("\n").filter((word) => word.trim()) : [];
    let filledCoordinates = [];
    if (puzzle.template?.filledCoordinates) {
        try {
            filledCoordinates = JSON.parse(puzzle.template.filledCoordinates);
        } catch (error) {
            Logger.error("Error parsing filledCoordinates:", error);
            filledCoordinates = [];
        }
    }
    const language = puzzle.language || puzzle.template?.language || "english";
    const { rows, cols } = resolveGridDims(puzzle);
    let grid = [];
    if (puzzle.letters) {
        grid = buildGridFromLetters(puzzle.letters, rows, cols, language);
    }

    filledCoordinates.forEach((coordinate) => {
        coordinate.positions?.forEach((pos) => {
            if (pos.value && grid[pos.row]?.[pos.col] !== undefined) {
                if (language === "french") {
                    grid[pos.row][pos.col] = normalizeFrenchWord(pos.value);
                } else {
                    grid[pos.row][pos.col] = pos.value.toUpperCase();
                }
            }
        });
    });

    return { wordsArray, filledCoordinates, grid, gridSize: cols };
}

/** Under UserLayout header; avoid height:100vh or the page exceeds the viewport and the header is off-screen. */
const BELOW_APP_HEADER_MIN = "calc(100vh - 5.5rem)";

const PLAY_TOOLBAR_ICON_BTN_SIZING_SX = {
    padding: "5px",
    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
};

/** Paid letter hint: reveal this many leading letters on the grid. */
const GRID_PAID_HINT_LETTER_COUNT = 2;

/** When the player has hint credits, briefly light up the hint button on this interval. */
const HINT_BUTTON_GLOW_INTERVAL_MS = 3 * 60 * 1000;
const HINT_BUTTON_GLOW_DURATION_MS = 2800;

const hintButtonLightOn = keyframes`
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: ${SURFACE.panel};
    color: ${ACCENT.light};
  }
  35% {
    box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.4), 0 0 18px rgba(255, 213, 79, 0.9);
    background-color: #FFF8E1;
    color: #F9A825;
  }
  65% {
    box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.28), 0 0 12px rgba(255, 213, 79, 0.65);
    background-color: #FFECB3;
    color: #FBC02D;
  }
`;

const MIST_PAGE_SX = {
    position: "relative",
    isolation: "isolate",
    width: "100%",
    flex: 1,
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    bgcolor: SITE_BG,
};

/** Status lines sit straight on the night backdrop, so they run light. */
const MIST_STATUS_TEXT_SX = { color: "rgba(226,232,240,0.86)" };

const FROSTED_PANEL_SX = {
    ...panelSurfaceSx,
    boxShadow: "0 8px 28px rgba(2, 6, 18, 0.45)",
};

function MistPlayShell({ children, sx: sxProp }) {
    const fitContent = sxProp?.flex === "0 0 auto";
    return (
        <Box sx={{ ...MIST_PAGE_SX, ...sxProp }}>
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: fitContent ? "0 0 auto" : 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: fitContent ? "auto" : 0,
                    width: "100%",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

/** Escape layout stacking (site header z-index) so fullscreen covers the viewport. */
function FullscreenPortal({ active, children }) {
    if (active && typeof document !== "undefined") {
        return createPortal(children, document.body);
    }
    return children;
}

function formatSolveDurationMs(ms) {
    if (ms == null || Number.isNaN(ms) || ms < 0) ms = 0;
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

/** accuracy = min(100, round((totalWords / attempts) * 100)), attempts ≥ 1 */
function computeSolveAccuracyPercent(totalWords, attempts) {
    const a = Math.max(Number(attempts) || 0, 1);
    const t = Math.max(Number(totalWords) || 0, 0);
    return Math.min(100, Math.round((t / a) * 100));
}

const GridPlayMain = ({
    gridTplId: propGridTplId,
    userSubscription: propUserSubscription,
    onPuzzleTitleChange: propOnPuzzleTitleChange,
    onFoundWordsChange,
    onPuzzleTypeChange,
    /** When route is `/story/play-grid/word_search/:id`, `useParams().type` is missing — pass `word_search` or `hidden_message`. */
    gridPlayRouteTypeSegment,
    /** Optional: embedded story-route behavior when path is not under `/story/play-grid` or flat `/puzzle/play-*-search|hidden-message`. */
    storyPlayLayout = false,
    /** When true (e.g. how-to page embed), back scrolls to top instead of navigating to /stories or browse. */
    howtoEmbed = false,
    /** Decrypted how-to sample JSON: skip grid API / play sync; same UI as live play */
    howtoSamplePuzzle = null,
}) => {
    const { t } = usePagesTranslation();
    const { onPuzzleTitleChange: contextOnPuzzleTitleChange, onPuzzleSubtitleChange: contextOnPuzzleSubtitleChange, hasSiteAdBanner } =
        useContext(UserLayoutContext);
    const onPuzzleTitleChange = propOnPuzzleTitleChange ?? contextOnPuzzleTitleChange;
    const onPuzzleSubtitleChange = contextOnPuzzleSubtitleChange;
    const { type, id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { triggerConfetti } = useConfetti();

    const isHowtoOfflineSample = Boolean(howtoSamplePuzzle);
    const isHowtoEmbedded = Boolean(howtoEmbed || isHowtoOfflineSample);
    const playMinHeight = isHowtoEmbedded || hasSiteAdBanner ? "auto" : BELOW_APP_HEADER_MIN;
    const playBodyFlex = isHowtoEmbedded || hasSiteAdBanner ? undefined : 1;
    const playShellSx = {
        minHeight: playMinHeight,
        ...(isHowtoEmbedded || hasSiteAdBanner ? { flex: "0 0 auto" } : {}),
    };
    /** How-to embeds these bodies on a white page; standalone play shows them on the night mist. */
    const statusTextSx = isHowtoEmbedded ? { color: "text.secondary" } : MIST_STATUS_TEXT_SX;

    const routeTypeSegment = useMemo(() => {
        let seg = gridPlayRouteTypeSegment ?? type;
        if (!seg && typeof location.pathname === "string") {
            if (location.pathname.includes("/puzzle/play-hidden-message/")) seg = "hidden_message";
            else if (location.pathname.includes("/puzzle/play-word-search/")) seg = "word_search";
        }
        return seg;
    }, [gridPlayRouteTypeSegment, type, location.pathname]);
    const routePuzzleType = useMemo(() => {
        const upper = routeTypeSegment ? String(routeTypeSegment).toUpperCase() : "WORD_SEARCH";
        return upper === "HIDDEN_MESSAGE" ? "HIDDEN_MESSAGE" : "WORD_SEARCH";
    }, [routeTypeSegment]);
    // Ensure gridTplId is always a string (UUID)
    const gridTplId = propGridTplId || String(id || '');
    const puzzleId = gridTplId; // Alias - some child components expect puzzleId

    const [wordsFound, setWordsFound] = useState([]);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [subscriptionTriggerReason] = useState('general'); // Used by SubscriptionDialog
    const [userSubscription, setUserSubscription] = useState(propUserSubscription || 'free');
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [loginDialogFeature, setLoginDialogFeature] = useState('hint'); // 'hint' or 'pdf'
    const [hiddenMessage, setHiddenMessage] = useState('');
    const [hiddenMessageLength, setHiddenMessageLength] = useState(0);
    const [hiddenMessageProgress, setHiddenMessageProgress] = useState(0);
    const [puzzleData, setPuzzleData] = useState(null);
    /** Prefer template/type (and hiddenMessage) from API so play UI matches the puzzle, not only the URL. */
    const puzzleType = useMemo(() => {
        if (!puzzleData) return routePuzzleType;
        const raw = String(puzzleData.type ?? puzzleData.template?.type ?? "")
            .toUpperCase()
            .replace(/\s+/g, "_");
        if (raw === "HIDDEN_MESSAGE") return "HIDDEN_MESSAGE";
        if (raw === "WORD_SEARCH") return "WORD_SEARCH";
        const hm = puzzleData.hiddenMessage ?? puzzleData.template?.hiddenMessage;
        if (hm != null && String(hm).trim() !== "") return "HIDDEN_MESSAGE";
        return routePuzzleType;
    }, [puzzleData, routePuzzleType]);

    usePlayRecord({
        puzzleType,
        puzzleId: gridTplId || null,
        enabled: !isHowtoOfflineSample && Boolean(gridTplId && puzzleData),
    });

    /** Word search + hidden message: free bulk first-letter mode is off; paid 2-letter hint is used instead. */
    const enableGridLetterHintsUi = false;
    const enablePaidLetterHint =
        puzzleType === "HIDDEN_MESSAGE" || puzzleType === "WORD_SEARCH";
    const gridBrowseListPath =
        puzzleType === "HIDDEN_MESSAGE" ? "/puzzles/hidden-message" : "/puzzles/word-search";
    const [loading, setLoading] = useState(true);
    const [clickedChips, setClickedChips] = useState(new Set());
    /** `/story/play-grid/*`: chip in Words to Find that matches last story vocabulary tap */
    const [wordsDrawerStoryHighlightWord, setWordsDrawerStoryHighlightWord] = useState(null);

    const [userInfo, setUserInfo] = useState(null);
    const [wordsDrawerOpen, setWordsDrawerOpen] = useState(false); // Start as false, will be set after settings load
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isBoldFont, setIsBoldFont] = useState(false);
    const [hintsEnabled, setHintsEnabled] = useState(false);
    /** In-flight hint-used words: shrink hint budget immediately so first-letter hints do not flash an extra cell before PUT returns. */
    const [gridHintUsedPendingWords, setGridHintUsedPendingWords] = useState([]);
    /** Paid hint: cells showing first N letters with a distinct background. */
    const [paidHintRevealCells, setPaidHintRevealCells] = useState([]);
    /** Normalized labels of words that received a paid letter hint (chip styling). */
    const [hintedWordKeys, setHintedWordKeys] = useState(() => new Set());
    const [letterHintBusy, setLetterHintBusy] = useState(false);
    const [letterHintConsumedVisible, setLetterHintConsumedVisible] = useState(false);
    const [hintButtonGlowing, setHintButtonGlowing] = useState(false);
    const letterHintConsumedTimerRef = useRef(null);
    const [soundEnabled, setSoundEnabled] = useState(true); // Controls sound effects
    const [bgmAutoPlay, setBgmAutoPlay] = useState(true);
    const [bgmPlaying, setBgmPlaying] = useState(false);
    const [settingsAnchor, setSettingsAnchor] = useState(null);
    const [timerPaused, setTimerPaused] = useState(false);
    const [walletPlayCredits, setWalletPlayCredits] = useState(null);
    const [walletHintCredits, setWalletHintCredits] = useState(null);
    const [walletPuzzleCoins, setWalletPuzzleCoins] = useState(null);
    const [walletBalancesLoading, setWalletBalancesLoading] = useState(false);
    const bgmButtonWrapRef = useRef(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");
    const [confettiTriggered, setConfettiTriggered] = useState(false);
    const [successCardOpen, setSuccessCardOpen] = useState(false);
    const [successCardStats, setSuccessCardStats] = useState({ time: "0:00", accuracy: 100 });
    const [successRating, setSuccessRating] = useState(0);
    const [successRatingBusy, setSuccessRatingBusy] = useState(false);
    const [startPlayCalled, setStartPlayCalled] = useState(false);
    const [playHistoryLoaded, setPlayHistoryLoaded] = useState(false); // Track if play history has been loaded
    const [settingsLoaded, setSettingsLoaded] = useState(false); // Track if user settings have been loaded
    const [typedWord, setTypedWord] = useState('');
    /** ADMIN: live tuning of grid letter font size (px). Mobile matches GridGrid `(max-width: 768px)`. */
    const [adminGridCellFontPx, setAdminGridCellFontPx] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches ? 14 : 18
    );
    const [highlightCells, setHighlightCells] = useState([]); // Array of {row, col, charIndex}
    const [showNetwork, setShowNetwork] = useState(false); // Track if network panel should be shown
    const [selectedNetworkWord, setSelectedNetworkWord] = useState(null); // Track selected word from network
    const [isAuthenticated, setIsAuthenticated] = useState(false); // Track user authentication status
    /** null | pending | ok | insufficient | error — premium word-puzzle credit charge */
    const [premiumCreditStatus, setPremiumCreditStatus] = useState(null);
    const [premiumCreditError, setPremiumCreditError] = useState("");
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false); // Track if feedback has been submitted to prevent showing again
    const [isPdfDownloading, setIsPdfDownloading] = useState(false); // Track if PDF is downloading
    const [pdfFormatDialogOpen, setPdfFormatDialogOpen] = useState(false); // PDF format (Letter/A4) dialog
    /** Words panel — Chat with AI (POST /home/assistant/help with gridId) */
    const [wordsDrawerAiMode, setWordsDrawerAiMode] = useState(false);
    const [assistantPrompt, setAssistantPrompt] = useState("");
    const [assistantLoading, setAssistantLoading] = useState(false);
    const [assistantHistoryLoading, setAssistantHistoryLoading] = useState(false);
    const [assistantMessages, setAssistantMessages] = useState([]);
    const [storyDialogOpen, setStoryDialogOpen] = useState(false);

    // Computed value: drawer should show only after settings loaded
    // Use useMemo to ensure this is computed consistently and appears immediately when conditions are met
    const shouldShowWordsDrawer = useMemo(() => {
        // Must wait for settings to load
        if (!settingsLoaded) return false;
        // Immersive fullscreen focuses the grid — hide the words panel until exit
        if (isFullscreen) return false;
        // Show if drawer is supposed to be open (set automatically in loadUserInfo)
        return wordsDrawerOpen;
    }, [settingsLoaded, wordsDrawerOpen, isFullscreen]);
    const puzzleGridRef = useRef(null);
    const initialSettingsLoadedRef = useRef(false); // Track if initial settings load is complete
    const playSessionStartMsRef = useRef(null);
    const puzzleAttemptsRef = useRef(0);
    const completionBaselineDoneRef = useRef(false);
    const prevSolvedCountRef = useRef(0);
    const successCardShownRef = useRef(false);
    /** One PUT /home/grid-play/complete-story per session when list + story are complete (retries on failure). */
    const storyCompleteStoryApiSentRef = useRef(false);

    useEffect(() => {
        if (!isFullscreen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                setIsFullscreen(false);
            }
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isFullscreen]);

    const playWordResolvedChime = useCallback(() => {
        if (!soundEnabled) return;
        AudioService.play(AUDIO_EFFECT.WORD_RESOLVED);
    }, [soundEnabled]);

    const [wordRevealFx, setWordRevealFx] = useState(null);
    const [celebrateCellIndexByKey, setCelebrateCellIndexByKey] = useState(null);
    const [bankLockIn, setBankLockIn] = useState(false);
    const wordRevealSeqRef = useRef(0);
    const celebrateClearTimerRef = useRef(null);
    const bankLockInTimerRef = useRef(null);

    const triggerWordRevealCelebration = useCallback((displayWord, cells) => {
        if (!displayWord || !Array.isArray(cells) || cells.length === 0) return;

        const celebrate = {};
        cells.forEach((c, i) => {
            if (c && typeof c.row === "number" && typeof c.col === "number") {
                celebrate[`${c.row},${c.col}`] = i;
            }
        });
        if (Object.keys(celebrate).length === 0) return;

        setCelebrateCellIndexByKey(celebrate);

        if (celebrateClearTimerRef.current) {
            window.clearTimeout(celebrateClearTimerRef.current);
        }
        celebrateClearTimerRef.current = window.setTimeout(() => {
            setCelebrateCellIndexByKey(null);
            celebrateClearTimerRef.current = null;
        }, WORD_REVEAL_ILLUMINATE_MS + cells.length * WORD_REVEAL_LETTER_STAGGER_MS + 80);

        requestAnimationFrame(() => {
            const cellRects = measureWordCellRects(cells);
            if (cellRects.length === 0) {
                setCelebrateCellIndexByKey(null);
                return;
            }
            wordRevealSeqRef.current += 1;
            setWordRevealFx({
                fxId: wordRevealSeqRef.current,
                word: String(displayWord).trim(),
                cellRects,
                bankRect: measureWordBankRect(),
            });
        });
    }, []);

    const handleWordRevealComplete = useCallback((fxId) => {
        setWordRevealFx((prev) => (prev?.fxId === fxId ? null : prev));
    }, []);

    const handleWordRevealBankImpact = useCallback(() => {
        setBankLockIn(true);
        if (bankLockInTimerRef.current) {
            window.clearTimeout(bankLockInTimerRef.current);
        }
        bankLockInTimerRef.current = window.setTimeout(() => {
            setBankLockIn(false);
            bankLockInTimerRef.current = null;
        }, WORD_REVEAL_BANK_BOUNCE_MS);
    }, []);

    useEffect(() => {
        return () => {
            if (celebrateClearTimerRef.current) {
                window.clearTimeout(celebrateClearTimerRef.current);
            }
            if (bankLockInTimerRef.current) {
                window.clearTimeout(bankLockInTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.WORD_RESOLVED);
        return () => AudioService.release(AUDIO_EFFECT.WORD_RESOLVED);
    }, []);

    useEffect(() => {
        trackEvent('entered', { name: 'GridPlayMain' });
    }, []);

    useLayoutEffect(() => {
        if (typeof window === "undefined" || isHowtoEmbedded) return;
        if (loading || !puzzleData) return;
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [loading, puzzleData, isHowtoEmbedded]);

    const isStoryPlayGridRoute =
        typeof location.pathname === "string" &&
        location.pathname.startsWith("/story/play-grid");
    const isPuzzlePlayGridRoute =
        typeof location.pathname === "string" &&
        (location.pathname.startsWith("/puzzle/play-grid") ||
            location.pathname.startsWith("/puzzle/play-hidden-message") ||
            location.pathname.startsWith("/puzzle/play-word-search"));

    const isHowtoWordGridPlayDemo =
        typeof location.pathname === "string" &&
        (location.pathname.includes("/howto/puzzle/word-search") ||
            location.pathname.includes("/howto/puzzle/hidden-message"));

    /** Story accordion + vocabulary markers + story↔chips highlighting (canonical routes and flat puzzle grid URLs). */
    const useStoryPlayGridUx =
        storyPlayLayout ||
        isStoryPlayGridRoute ||
        isHowtoWordGridPlayDemo ||
        (isPuzzlePlayGridRoute &&
            (puzzleType === "WORD_SEARCH" || puzzleType === "HIDDEN_MESSAGE"));

    const gridPageDisplayTitle = useMemo(() => {
        if (!puzzleData) return "";
        if (!useStoryPlayGridUx) {
            return puzzleData.title || "";
        }
        const src = gridStorySourceRecord(puzzleData);
        if (src) {
            const { title } = getLocalizedStoryFromTemplate(src);
            const trimmed = typeof title === "string" ? title.trim() : "";
            if (trimmed) return trimmed;
        }
        return puzzleData.title || "";
    }, [puzzleData, useStoryPlayGridUx]);

    const gridTypePath = puzzleType === "HIDDEN_MESSAGE" ? "hidden_message" : "word_search";
    const previewBase = process.env.REACT_APP_PREVIEW_IMAGE_BASE;
    const gridPreviewImage =
        puzzleData?.previewImageUrl ||
        puzzleData?.template?.previewImageUrl ||
        (gridTplId ? `${previewBase}/${gridTypePath}/${gridTplId}.png` : "");
    usePageTitle(
        isHowtoOfflineSample
            ? ""
            : gridPageDisplayTitle ||
                  puzzleData?.title ||
                  (puzzleType === "HIDDEN_MESSAGE" ? "Hidden Message" : "Word Search")
    );

    /** Same order as Words to Find / progress — not filledCoordinates order — so finding a word does not “promote” a new word into the hint set. */
    const hintOrderedWords = useMemo(() => {
        if (!puzzleData?.words?.length) return null;
        if (puzzleType === 'HIDDEN_MESSAGE' && puzzleData.hiddenMessage) {
            return puzzleData.words.filter(
                w => w.toLowerCase() !== puzzleData.hiddenMessage.toLowerCase()
            );
        }
        return puzzleData.words;
    }, [puzzleData?.words, puzzleType, puzzleData?.hiddenMessage]);

    const gridHintSlotBudget = useMemo(() => {
        const huStr = puzzleData?.template?.hintUsed ?? puzzleData?.hintUsed;
        const effective = hintUsedStringWithPendingAppends(huStr, gridHintUsedPendingWords);
        return hintRevealSlotCount(effective, GRID_HINT_WORD_LIMIT);
    }, [puzzleData?.template?.hintUsed, puzzleData?.hintUsed, gridHintUsedPendingWords]);

    const gridHintButtonTooltip = useMemo(() => {
        if (hintsEnabled) return t("puzzle.playGrid.hide-hints");
        if (gridHintSlotBudget <= 0) {
            return t("puzzle.playGrid.hints-exhausted", { limit: GRID_HINT_WORD_LIMIT });
        }
        return t("puzzle.playGrid.show-hints");
    }, [hintsEnabled, gridHintSlotBudget, t]);

    const recordGridPlayHintUsedIfApplicable = useCallback(
        (wordToAddNormalized, wordsFoundBeforeSolve, solvedWordForApi) => {
            if (!hintsEnabled || !userInfo?.cloudUser?.id || !puzzleData?.filledCoordinates) return;
            const huStr = puzzleData?.template?.hintUsed ?? puzzleData?.hintUsed;
            const effectiveBeforeThis = hintUsedStringWithPendingAppends(huStr, gridHintUsedPendingWords);
            const slots = hintRevealSlotCount(effectiveBeforeThis, GRID_HINT_WORD_LIMIT);
            const targets = getGridHintTargetWordsNormalized(
                puzzleData.filledCoordinates,
                wordsFoundBeforeSolve,
                hintOrderedWords,
                slots
            );
            if (!targets.has(wordToAddNormalized)) return;
            const word =
                solvedWordForApi != null && String(solvedWordForApi).trim()
                    ? String(solvedWordForApi).trim()
                    : wordToAddNormalized;
            setGridHintUsedPendingWords((prev) => [...prev, word]);
            GridService.hintUsed(gridTplId, { word })
                .then((ro) => {
                    const hu = hintUsedStringFromPlayRo(ro);
                    if (hu !== undefined) {
                        setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                    }
                    setGridHintUsedPendingWords((prev) => {
                        const i = prev.indexOf(word);
                        if (i === -1) return prev;
                        return [...prev.slice(0, i), ...prev.slice(i + 1)];
                    });
                })
                .catch((err) => {
                    setGridHintUsedPendingWords((prev) => {
                        const i = prev.indexOf(word);
                        if (i === -1) return prev;
                        return [...prev.slice(0, i), ...prev.slice(i + 1)];
                    });
                    Logger.error("Error recording hint use:", err);
                });
        },
        [
            hintsEnabled,
            userInfo?.cloudUser?.id,
            puzzleData?.filledCoordinates,
            puzzleData?.template?.hintUsed,
            puzzleData?.hintUsed,
            hintOrderedWords,
            gridHintUsedPendingWords,
            gridTplId,
        ]
    );

    // Check if puzzle has word network data
    const hasWordNetwork = useMemo(() => {
        const wordNetwork = puzzleData?.template?.wordNetwork || puzzleData?.wordNetwork;
        return wordNetwork?.nodes && wordNetwork?.links;
    }, [puzzleData]);

    // Get word network data (filter out hidden message for HIDDEN_MESSAGE puzzles)
    const wordNetworkData = useMemo(() => {
        const network = puzzleData?.template?.wordNetwork || puzzleData?.wordNetwork;
        if (!network || !network.nodes || !network.links) {
            return network;
        }

        // For HIDDEN_MESSAGE puzzles, filter out the hidden message from the network
        if (puzzleType === 'HIDDEN_MESSAGE' && puzzleData?.hiddenMessage) {
            const hiddenMessageLower = puzzleData.hiddenMessage.toLowerCase();
            
            // Filter out nodes that match the hidden message
            const filteredNodes = network.nodes.filter(node => {
                const nodeLabel = (node.label || node.id || '').toLowerCase();
                return nodeLabel !== hiddenMessageLower;
            });

            // Get IDs of filtered nodes
            const filteredNodeIds = new Set(filteredNodes.map(node => node.id));

            // Filter out links that connect to/from the hidden message node
            const filteredLinks = network.links.filter(link => {
                return filteredNodeIds.has(link.source) && filteredNodeIds.has(link.target);
            });

            return {
                ...network,
                nodes: filteredNodes,
                links: filteredLinks,
                metadata: network.metadata ? {
                    ...network.metadata,
                    nodeCount: filteredNodes.length,
                    edgeCount: filteredLinks.length
                } : undefined
            };
        }

        return network;
    }, [puzzleData, puzzleType]);

    // Calculate hidden message cells when message is revealed
    const hiddenMessageCells = useMemo(() => {
        if (!hiddenMessage || !puzzleData || puzzleType !== "HIDDEN_MESSAGE") {
            return [];
        }
        const cells = findWordInGrid(hiddenMessage, puzzleData.filledCoordinates);
        return cells || [];
    }, [hiddenMessage, puzzleData, puzzleType]);

    /**
     * Completion for grid puzzles: count puzzle words that match `wordsFound` using the same rules as gameplay
     * (hyphens / French normalization).
     */
    const gridListWordsCompletion = useMemo(() => {
        if (!puzzleData?.words?.length) {
            return { solvedCount: 0, total: 0, isComplete: false };
        }
        const wordsToCheck =
            puzzleType === "HIDDEN_MESSAGE"
                ? puzzleData.words.filter(
                      (word) => word.toLowerCase() !== puzzleData?.hiddenMessage?.toLowerCase()
                  )
                : puzzleData.words;
        const total = wordsToCheck.length;
        if (total <= 0) {
            return { solvedCount: 0, total: 0, isComplete: false };
        }
        const solvedCount = wordsToCheck.filter((w) => isWordAlreadyFound(w, wordsFound)).length;
        return {
            solvedCount,
            total,
            isComplete: solvedCount === total,
        };
    }, [puzzleData, puzzleType, wordsFound]);

    const isWordSearchComplete = puzzleType === "WORD_SEARCH" && gridListWordsCompletion.isComplete;

    const canDownloadPdf = !isHowtoOfflineSample;
    const pdfSolvedCurrent = gridListWordsCompletion.solvedCount;
    const pdfWordsTotal = gridListWordsCompletion.total;

    const { visibilityPaused, continuePlaying } = usePlayVisibilityPause({
        enabled: Boolean(puzzleData) && !loading && !gridListWordsCompletion.isComplete,
    });
    const timerRunning =
        !gridListWordsCompletion.isComplete && !timerPaused && !visibilityPaused;

    // Access: Easy → anyone; catalog-free puzzles → every difficulty; otherwise Medium/Hard need sign-in.
    const wordDifficulty =
        puzzleData?.difficultyLevel
        ?? puzzleData?.template?.difficultyLevel
        ?? null;
    const markedFree = isCatalogFreePuzzle(puzzleData);
    const isPuzzleFree = markedFree || isWordPuzzleOpenAccess(wordDifficulty);
    const accessContext = useMemo(() => buildPuzzleAccessContext(userInfo), [userInfo]);
    const canAccessPuzzle = useMemo(
        () => canAccessWordPuzzle(wordDifficulty, {
            ...accessContext,
            // Howto offline samples skip auth/credit gates
            isAuthenticated: isHowtoOfflineSample ? true : accessContext.isAuthenticated,
            isStaff: isHowtoOfflineSample ? true : accessContext.isStaff,
        }, { isFreePuzzle: markedFree }),
        [wordDifficulty, accessContext, isHowtoOfflineSample, markedFree],
    );
    const isPaidPuzzleNeedingUserCheck = puzzleData && !isPuzzleFree && !isHowtoOfflineSample;
    // Guest on Medium/Hard: wait for auth check. Credit paywall is separate.
    const showAccessCheck = isPaidPuzzleNeedingUserCheck && !isAuthenticated;

    const needsPremiumCreditCharge = useMemo(() => {
        if (isHowtoOfflineSample || markedFree || !gridTplId || !puzzleData || !canAccessPuzzle) return false;
        if (accessContext.isStaff) return false;
        return isWordPuzzleCreditGated(wordDifficulty);
    }, [
        isHowtoOfflineSample,
        markedFree,
        gridTplId,
        puzzleData,
        canAccessPuzzle,
        accessContext.isStaff,
        wordDifficulty,
    ]);

    useEffect(() => {
        let cancelled = false;

        async function chargePremiumCredit() {
            if (!needsPremiumCreditCharge || !gridTplId) {
                setPremiumCreditStatus(needsPremiumCreditCharge ? null : "ok");
                setPremiumCreditError("");
                return;
            }
            setPremiumCreditStatus("pending");
            setPremiumCreditError("");
            try {
                const result = await HomeCreditService.consumePremiumPlay(gridTplId);
                if (cancelled) return;
                setPremiumCreditStatus("ok");
                if (result.creditsConsumed > 0) {
                    trackEvent("word_puzzle_premium_credit_consumed", {
                        puzzle_type: puzzleType,
                        puzzle_id: gridTplId,
                        credits_consumed: result.creditsConsumed,
                        credit_balance: result.creditBalance,
                    });
                }
            } catch (err) {
                if (cancelled) return;
                const message = err?.message || "";
                Logger.warn("Grid premium credit consume failed:", message);
                if (/insufficient/i.test(message)) {
                    setPremiumCreditStatus("insufficient");
                    setSubscriptionModalOpen(true);
                } else {
                    setPremiumCreditStatus("error");
                    setPremiumCreditError(message);
                }
            }
        }

        chargePremiumCredit();
        return () => {
            cancelled = true;
        };
    }, [needsPremiumCreditCharge, gridTplId, puzzleType]);

    /** Feedback / Complete after solve: only for logged-in users (guests see disabled controls). */
    const completionActionsDisabled = !settingsLoaded || !isAuthenticated;

    /** Story accordion + prose tokens (parity with `CrossPlayMain` story play). */
    const storyPresentation = useMemo(() => {
        const empty = {
            titleAboveGrid: "",
            bodyText: "",
            imageUrl: "",
            matchSet: new Set(),
            tokenToClueNumber: new Map(),
            storyId: null,
            showSlidingPuzzle: false,
            showJigsawPuzzle: false,
        };
        if (!useStoryPlayGridUx || !puzzleData) return empty;
        const src = gridStorySourceRecord(puzzleData);
        if (!src) return empty;
        const { title, text, imageUrl } = getLocalizedStoryFromTemplate(src);
        const titleAboveGrid = typeof title === "string" ? title.trim() : "";
        const bodyText = typeof text === "string" ? text : "";
        const imageUrlStr = typeof imageUrl === "string" ? imageUrl : "";
        const coords = puzzleData.filledCoordinates || [];
        const matchSet = buildPuzzleWordMatchSet(coords);
        const tokenToClueNumber = buildStoryTokenToClueNumberMap(coords);
        const storyObj = src.story || puzzleData.template?.story;
        const storyId = storyObj?.id ? String(storyObj.id).trim() : null;
        const showSlidingPuzzle = Boolean(storyId && storyObj?.sliding?.state === "PUBLISHED");
        const showJigsawPuzzle = Boolean(storyId && storyObj?.jigsaw?.state === "PUBLISHED");
        return { titleAboveGrid, bodyText, imageUrl: imageUrlStr, matchSet, tokenToClueNumber, storyId, showSlidingPuzzle, showJigsawPuzzle };
    }, [useStoryPlayGridUx, puzzleData]);

    const hasPlayStoryContent = Boolean(
        useStoryPlayGridUx &&
            (String(storyPresentation.titleAboveGrid || "").trim() ||
                String(storyPresentation.imageUrl || "").trim() ||
                Boolean(storyPresentation.bodyText && String(storyPresentation.bodyText).trim()))
    );

    const storyShareIdFromPayload = useMemo(
        () => resolveStoryShareIdFromPlayPayload(puzzleData),
        [puzzleData]
    );
    const storyShareTitleForShare = useMemo(() => {
        const st = String(storyPresentation.titleAboveGrid || "").trim();
        if (st) return st;
        return String(gridPageDisplayTitle || "").trim();
    }, [storyPresentation.titleAboveGrid, gridPageDisplayTitle]);

    const gridStoryWordObjs = useMemo(() => {
        if (!puzzleData?.template) return [];
        return collectWordObjsForStoryMask({
            filledCoordinates: puzzleData.filledCoordinates,
            gridTpl: puzzleData.template,
            template: puzzleData.template,
        });
    }, [puzzleData]);

    const gridHiddenMessageStoryGate = useMemo(() => {
        if (puzzleType !== "HIDDEN_MESSAGE" || !puzzleData?.hiddenMessage) return null;
        return {
            revealed: Boolean(hiddenMessage),
            phrase: String(puzzleData.hiddenMessage),
        };
    }, [puzzleType, puzzleData?.hiddenMessage, hiddenMessage]);

    const gridStoryDialogChildren = useMemo(() => {
        if (!useStoryPlayGridUx || !storyPresentation.bodyText?.trim()) return null;
        const isHiddenMessage = puzzleType === "HIDDEN_MESSAGE";
        return (
            <MaskedStorySnippet
                text={storyPresentation.bodyText}
                wordObjs={gridStoryWordObjs}
                highlightMode="underline"
                filledCoordinates={puzzleData?.filledCoordinates}
                resolvedWordsFound={wordsFound}
                hiddenMessageStoryGate={gridHiddenMessageStoryGate}
                resolvedHighlightVariant={isHiddenMessage ? "blue" : "green"}
                underlineColor={isHiddenMessage ? "#1565c0" : "#3BA666"}
                component="span"
                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}
            />
        );
    }, [
        useStoryPlayGridUx,
        storyPresentation.bodyText,
        gridStoryWordObjs,
        puzzleData?.filledCoordinates,
        wordsFound,
        gridHiddenMessageStoryGate,
        puzzleType,
    ]);

    const gridStoryAccordion = useMemo(() => {
        if (!useStoryPlayGridUx || !gridStoryDialogChildren) return null;
        const summaryTitle = String(storyPresentation.titleAboveGrid || "").trim()
            ? String(storyPresentation.titleAboveGrid).trim()
            : t("puzzle.playCross.story-card-heading");
        return (
            <StoryPlayStoryAccordion
                summaryTitle={summaryTitle}
                storyId={storyPresentation.storyId ?? undefined}
                showSlidingPuzzle={storyPresentation.showSlidingPuzzle}
                showJigsawPuzzle={storyPresentation.showJigsawPuzzle}
                defaultExpanded={false}
                compact
            >
                {gridStoryDialogChildren}
            </StoryPlayStoryAccordion>
        );
    }, [
        useStoryPlayGridUx,
        gridStoryDialogChildren,
        storyPresentation.titleAboveGrid,
        storyPresentation.storyId,
        storyPresentation.showSlidingPuzzle,
        storyPresentation.showJigsawPuzzle,
        t,
    ]);

    useEffect(() => {
        storyCompleteStoryApiSentRef.current = false;
    }, [gridTplId]);

    useEffect(() => {
        if (!gridListWordsCompletion.isComplete) {
            storyCompleteStoryApiSentRef.current = false;
        }
    }, [gridListWordsCompletion.isComplete]);

    useEffect(() => {
        if (!hasPlayStoryContent || !gridListWordsCompletion.isComplete) return;
        if (!gridTplId || howtoSamplePuzzle || !userInfo?.cloudUser?.id) return;
        if (storyCompleteStoryApiSentRef.current) return;
        storyCompleteStoryApiSentRef.current = true;
        GridService.completeStory(gridTplId, true).catch((err) => {
            Logger.error("Grid complete-story:", err);
            storyCompleteStoryApiSentRef.current = false;
        });
    }, [
        hasPlayStoryContent,
        gridListWordsCompletion.isComplete,
        gridTplId,
        howtoSamplePuzzle,
        userInfo?.cloudUser?.id,
    ]);

    const isAdminRole = userInfo?.cloudUser?.userRole?.toUpperCase() === "ADMIN";

    /** Puzzle Assistant (Chat with AI): logged-in users who can play this puzzle */
    const canUseAssistant = !isHowtoOfflineSample && isAuthenticated && canAccessPuzzle;
    /** Show magic icon for guests who can play (e.g. free puzzle); gated chat until sign-in */
    const showAssistantMagic =
        !isHowtoOfflineSample && (canUseAssistant || (!isAuthenticated && canAccessPuzzle));

    const loadAssistantChatHistory = useCallback(async () => {
        if (!isAuthenticated || !gridTplId) return;
        setAssistantHistoryLoading(true);
        try {
            const data = await HomeService.getAssistantChatHistory({
                page: 0,
                pageSize: 50,
                gridId: gridTplId,
            });
            setAssistantMessages(Array.isArray(data?.content) ? data.content : []);
        } catch (err) {
            Logger.error("Assistant chat history:", err);
            setAssistantMessages([]);
        } finally {
            setAssistantHistoryLoading(false);
        }
    }, [isAuthenticated, gridTplId]);

    useEffect(() => {
        if (!canUseAssistant || !wordsDrawerAiMode || !gridTplId) return;
        loadAssistantChatHistory();
    }, [canUseAssistant, wordsDrawerAiMode, gridTplId, loadAssistantChatHistory]);

    const handleAssistantSubmit = useCallback(async () => {
        if (!canUseAssistant) return;
        const creditsTotal = userInfo?.cloudUser?.assistantCreditsTotal;
        if (typeof creditsTotal === "number" && creditsTotal <= 0) {
            setSnackbarMessage(
                t(
                    "puzzle.playCross.assistant-credits-empty",
                    "You're out of Coins. Add a subscription or coin pack to keep using the assistant."
                )
            );
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }
        const text = assistantPrompt.trim();
        if (!text || !gridTplId) {
            setSnackbarMessage(t("puzzle.playCross.assistant-empty", "Enter a message for the assistant."));
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }
        setAssistantLoading(true);
        try {
            await HomeService.postAssistantHelp({ message: text, gridId: gridTplId });
            setAssistantPrompt("");
            await loadAssistantChatHistory();
        } catch (err) {
            const msg = err?.message || "Assistant request failed";
            setSnackbarMessage(msg);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Assistant help error:", err);
        } finally {
            setAssistantLoading(false);
        }
    }, [canUseAssistant, assistantPrompt, gridTplId, t, loadAssistantChatHistory, userInfo?.cloudUser?.assistantCreditsTotal]);

    useEffect(() => {
        const loadPuzzle = async () => {
            if (howtoSamplePuzzle) {
                try {
                    const puzzle = howtoSamplePuzzle;
                    const { wordsArray, filledCoordinates, grid, gridSize } =
                        buildProcessedGridFieldsFromRawPuzzle(puzzle);
                    setPuzzleData({
                        ...puzzle,
                        words: wordsArray,
                        filledCoordinates,
                        grid,
                        gridSize,
                        free: true,
                        isFree: true,
                    });
                    setUserSubscription('free');
                } catch (error) {
                    Logger.error('Error loading how-to sample puzzle:', error);
                    setPuzzleData(null);
                } finally {
                    setLoading(false);
                }
                return;
            }

            if (!gridTplId) {
                Logger.error('No puzzle ID provided');
                setPuzzleData(null);
                setLoading(false);
                return;
            }

            try {
                const puzzle = await GridService.getPuzzleById(gridTplId);
                if (!puzzle) {
                    Logger.error('Puzzle not found for ID:', gridTplId);
                    setPuzzleData(null);
                    setLoading(false);
                    return;
                }

                // Parse words string into array
                const wordsArray = puzzle.words ? puzzle.words.split('\n').filter(word => word.trim()) : [];

                // Parse filledCoordinates string into array
                let filledCoordinates = [];
                if (puzzle.template?.filledCoordinates) {
                    try {
                        filledCoordinates = JSON.parse(puzzle.template.filledCoordinates);
                    } catch (error) {
                        Logger.error('Error parsing filledCoordinates:', error);
                        filledCoordinates = [];
                    }
                }

                // Get language for normalization
                const language = puzzle.language || puzzle.template?.language || 'english';
                
                // Create grid from letters string (Mini e.g. 6×6 or Classic 12×12)
                const { rows, cols } = resolveGridDims(puzzle);
                const gridSize = cols;
                let grid = [];
                if (puzzle.letters) {
                    grid = buildGridFromLetters(puzzle.letters, rows, cols, language);
                }

                // Apply filledCoordinates to grid
                // Normalize characters for French puzzles (remove accents)
                filledCoordinates.forEach(coordinate => {
                    coordinate.positions?.forEach(pos => {
                        if (pos.value && grid[pos.row]?.[pos.col] !== undefined) {
                            // Normalize French characters (remove accents) - function already converts to uppercase
                            if (language === 'french') {
                                grid[pos.row][pos.col] = normalizeFrenchWord(pos.value);
                            } else {
                                grid[pos.row][pos.col] = pos.value.toUpperCase();
                            }
                        }
                    });
                });

                // Create processed puzzle data
                setPuzzleData({
                    ...puzzle,
                    words: wordsArray,
                    filledCoordinates,
                    grid,
                    gridSize
                });
                setUserSubscription(isWordPuzzleOpenAccess(puzzle.difficultyLevel) ? 'free' : 'premium');
            } catch (error) {
                Logger.error('Error loading puzzle:', error);
                setPuzzleData(null);
            } finally {
                setLoading(false);
            }
        };

        loadPuzzle();
    }, [puzzleType, gridTplId, howtoSamplePuzzle]);

    // Clock started_at when play session is ready (used for SuccessCard duration).
    useEffect(() => {
        if (!puzzleData || !playHistoryLoaded || isHowtoOfflineSample) return;
        if (playSessionStartMsRef.current == null) {
            playSessionStartMsRef.current = Date.now();
        }
    }, [puzzleData, playHistoryLoaded, isHowtoOfflineSample]);

    // Reset startPlayCalled flag when puzzle changes
    useEffect(() => {
        setStartPlayCalled(false);
        setPlayHistoryLoaded(false);
        setWordsDrawerStoryHighlightWord(null);
        setStoryDialogOpen(false);
        setConfettiTriggered(false);
        setSuccessCardOpen(false);
        setSuccessRating(0);
        setSuccessRatingBusy(false);
        playSessionStartMsRef.current = null;
        puzzleAttemptsRef.current = 0;
        completionBaselineDoneRef.current = false;
        prevSolvedCountRef.current = 0;
        successCardShownRef.current = false;
    }, [gridTplId]);

    // Mark cells as found when play history is loaded and words are restored
    useEffect(() => {
        if (!playHistoryLoaded || !puzzleData || wordsFound.length === 0) {
            return;
        }

        // Use a small timeout to ensure puzzleGridRef is ready
        const timeoutId = setTimeout(() => {
            if (!puzzleGridRef.current) return;
            
            wordsFound.forEach(word => {
                const cells = findWordInGrid(word, puzzleData.filledCoordinates);
                if (cells?.length > 0) {
                    puzzleGridRef.current.markClickedCellsAsFound(cells);
                }
            });
        }, 150);

        return () => clearTimeout(timeoutId);
    }, [playHistoryLoaded, puzzleData, wordsFound]);

    // Call startPlay to get user's play history when puzzle loads
    useEffect(() => {
        const callStartPlay = async () => {
            if (howtoSamplePuzzle) {
                setPlayHistoryLoaded(true);
                setStartPlayCalled(true);
                return;
            }
            // Only call if puzzle data is loaded, gridTplId exists, and we haven't called it yet
            if (!gridTplId || !puzzleData || startPlayCalled) {
                return;
            }

            try {
                // GET /home/grid/:id sets hwPlay (latest session, including completed). /home/grid-play/start rejects completed puzzles.
                const fromDetail = puzzleData.hwPlay;
                if (fromDetail) {
                    const completed = fromDetail.isCompleted === true;
                    const wfRaw = fromDetail.wordsFound ?? fromDetail.foundWords;
                    const hasStoredWords = wfRaw != null && String(wfRaw).trim() !== "";
                    if (completed || hasStoredWords) {
                        const restoredWords = wfRaw != null ? parseWordsFound(wfRaw) : [];
                        if (restoredWords.length > 0) {
                            setWordsFound(restoredWords);
                        }
                        const hu = hintUsedStringFromPlayRo(fromDetail);
                        if (hu !== undefined) {
                            setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                        }
                        setPlayHistoryLoaded(true);
                        setStartPlayCalled(true);
                        return;
                    }
                }

                // Call startPlay API to get user's play history
                const playHistory = await GridService.startPlay(gridTplId);
                
                if (playHistory) {
                    // Restore found words from play history
                    const restoredWords = playHistory.wordsFound 
                        ? parseWordsFound(playHistory.wordsFound)
                        : playHistory.foundWords 
                            ? parseWordsFound(playHistory.foundWords) // Backward compatibility
                            : Array.isArray(playHistory) 
                                ? parseWordsFound(playHistory)
                                : [];
                    
                    if (restoredWords.length > 0) {
                        setWordsFound(restoredWords);
                    }
                    const hu = hintUsedStringFromPlayRo(playHistory);
                    if (hu !== undefined) {
                        setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                    }
                }
                setPlayHistoryLoaded(true);
                
                // Mark as called to prevent multiple calls
                setStartPlayCalled(true);
            } catch (error) {
                Logger.error('Error calling startPlay:', error);
                // Mark as called even on error to prevent retry loops
                setStartPlayCalled(true);
                setPlayHistoryLoaded(true);
            }
        };

        callStartPlay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridTplId, puzzleData]);

    // Handle user info loading
    useEffect(() => {
        const loadUserInfo = async () => {
            if (!gridTplId) return;
            
            if (!puzzleData) {
                // Don't set settingsLoaded here — wait until we have puzzleData and have checked user access
                return;
            }

            try {
                const userData = await UserService.getUserInfo();
                setUserInfo(userData);
                
                // Check authentication status
                const authStatus = await AuthService.isAuthenticated();
                setIsAuthenticated(authStatus);
                
                // Load settings (defaults if no user data)
                const settingsData = userData ? UserService.getSettingsFormData(userData) : { soundEnabled: true };
                const soundEnabledSetting = settingsData.soundEnabled !== undefined ? settingsData.soundEnabled : true;
                const shouldOpenDrawer = true; // Show "Words to Find" by default on both mobile and desktop
                
                // Set states together - React 18 automatically batches these
                setSoundEnabled(soundEnabledSetting);
                setBgmAutoPlay(settingsData.bgmAutoPlay);
                setWordsDrawerOpen(shouldOpenDrawer);
                setSettingsLoaded(true);
                initialSettingsLoadedRef.current = true;
            } catch (error) {
                Logger.error('Error loading user info:', error);
                // On error, check authentication status
                try {
                    const authStatus = await AuthService.isAuthenticated();
                    setIsAuthenticated(authStatus);
                } catch (authError) {
                    setIsAuthenticated(false);
                }
                // On error, use defaults (show drawer, soundEnabled = true)
                const shouldOpenDrawer = true;
                setSoundEnabled(true);
                setBgmAutoPlay(true);
                setWordsDrawerOpen(shouldOpenDrawer);
                setSettingsLoaded(true);
                initialSettingsLoadedRef.current = true;
            }
        };

        loadUserInfo();
    }, [gridTplId, puzzleData, isMobile]);

    const loadWalletBalances = useCallback(async () => {
        if (!isAuthenticated) {
            setWalletPlayCredits(null);
            setWalletHintCredits(null);
            setWalletPuzzleCoins(null);
            return;
        }
        setWalletBalancesLoading(true);
        try {
            const [play, hints, info] = await Promise.all([
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_CODE).catch(() => null),
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_HINTS_CODE).catch(() => null),
                UserService.getUserInfo().catch(() => null),
            ]);
            setWalletPlayCredits(play?.creditBalance ?? 0);
            setWalletHintCredits(hints?.creditBalance ?? 0);
            setWalletPuzzleCoins(resolveTotalPuzzleCoins(info?.cloudUser));
        } catch (e) {
            Logger.warn("GridPlayMain load wallet:", e);
        } finally {
            setWalletBalancesLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!settingsAnchor || !isAuthenticated) return;
        void loadWalletBalances();
    }, [settingsAnchor, isAuthenticated, loadWalletBalances]);

    useEffect(() => {
        if (!isAuthenticated) return;
        void loadWalletBalances();
    }, [isAuthenticated, loadWalletBalances]);

    const persistPlaySetting = useCallback(async (patch) => {
        try {
            const info = await UserService.getUserInfo();
            const id = userInfo?.cloudUser?.id || info?.cloudUser?.id;
            if (!id) return;
            await UserService.updateUserProfile(id, {
                ...UserService.getSettingsFormData(info),
                ...patch,
            });
        } catch (e) {
            Logger.warn("GridPlayMain save setting:", e);
        }
    }, [userInfo?.cloudUser?.id]);

    const handleToggleSoundEffects = useCallback(() => {
        setSoundEnabled((prev) => {
            const next = !prev;
            void persistPlaySetting({ soundEnabled: next });
            return next;
        });
    }, [persistPlaySetting]);

    const hasHintCoins = Number(walletPuzzleCoins) > 0;

    useEffect(() => {
        setPaidHintRevealCells([]);
        setHintedWordKeys(new Set());
        setLetterHintBusy(false);
        setLetterHintConsumedVisible(false);
        if (letterHintConsumedTimerRef.current) {
            window.clearTimeout(letterHintConsumedTimerRef.current);
            letterHintConsumedTimerRef.current = null;
        }
    }, [gridTplId]);

    useEffect(() => {
        if (!enablePaidLetterHint || !hasHintCoins || gridListWordsCompletion.isComplete) {
            setHintButtonGlowing(false);
            return undefined;
        }

        let glowOffTimer = null;
        const startGlow = () => {
            setHintButtonGlowing(true);
            if (glowOffTimer) window.clearTimeout(glowOffTimer);
            glowOffTimer = window.setTimeout(() => {
                setHintButtonGlowing(false);
                glowOffTimer = null;
            }, HINT_BUTTON_GLOW_DURATION_MS);
        };

        startGlow();
        const intervalId = window.setInterval(startGlow, HINT_BUTTON_GLOW_INTERVAL_MS);
        return () => {
            window.clearInterval(intervalId);
            if (glowOffTimer) window.clearTimeout(glowOffTimer);
        };
    }, [enablePaidLetterHint, hasHintCoins, gridListWordsCompletion.isComplete]);

    const handlePaidLetterHintClick = useCallback(async () => {
        if (!enablePaidLetterHint || gridListWordsCompletion.isComplete || letterHintBusy) return;

        if (!isAuthenticated) {
            setLoginDialogFeature("hint");
            setLoginDialogOpen(true);
            return;
        }

        const listWords = Array.isArray(hintOrderedWords) ? hintOrderedWords : [];
        const unresolved = listWords.filter((w) => !isWordAlreadyFound(w, wordsFound));
        if (unresolved.length === 0) {
            setSnackbarMessage(t("puzzle.playGrid.letter-hint-none"));
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
            return;
        }

        const clickedUnresolved = unresolved.find((w) => clickedChips.has(w));
        const candidates = unresolved.filter((w) => !hintedWordKeys.has(normalizeWord(w)));
        const pickFrom = clickedUnresolved && !hintedWordKeys.has(normalizeWord(clickedUnresolved))
            ? [clickedUnresolved]
            : candidates.length > 0
              ? candidates
              : [];

        if (pickFrom.length === 0) {
            setSnackbarMessage(t("puzzle.playGrid.letter-hint-already"));
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
            return;
        }

        const targetWord = pickFrom[0];
        const coords = Array.isArray(puzzleData?.filledCoordinates)
            ? puzzleData.filledCoordinates
            : [];
        const coord = coords.find((c) => {
            const label = (c.text || c.word || "").trim();
            return label && normalizeWord(label) === normalizeWord(targetWord);
        });
        const cells = gridCoordGetFirstNPositions(coord, GRID_PAID_HINT_LETTER_COUNT);
        if (cells.length === 0) {
            setSnackbarMessage(t("puzzle.playGrid.letter-hint-failed"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        setLetterHintBusy(true);
        try {
            const spend = await HomePuzzleCoinsService.spendForHint({
                featureUsed: "grid_letter_hint",
                coins: 1,
            });
            if (typeof spend?.totalPuzzleCoins === "number") {
                setWalletPuzzleCoins(spend.totalPuzzleCoins);
            } else {
                void loadWalletBalances();
            }

            setPaidHintRevealCells((prev) => {
                const map = new Map(prev.map((c) => [`${c.row},${c.col}`, c]));
                cells.forEach((c) => map.set(`${c.row},${c.col}`, c));
                return [...map.values()];
            });
            setHintedWordKeys((prev) => {
                const next = new Set(prev);
                next.add(normalizeWord(targetWord));
                return next;
            });
            setClickedChips((prev) => {
                const next = new Set(prev);
                next.add(targetWord);
                return next;
            });
            setWordsDrawerOpen(true);

            setLetterHintConsumedVisible(true);
            if (letterHintConsumedTimerRef.current) {
                window.clearTimeout(letterHintConsumedTimerRef.current);
            }
            letterHintConsumedTimerRef.current = window.setTimeout(() => {
                setLetterHintConsumedVisible(false);
                letterHintConsumedTimerRef.current = null;
            }, 4000);

            const wordLabel = String(targetWord).trim();
            if (wordLabel && userInfo?.cloudUser?.id) {
                setGridHintUsedPendingWords((prev) => [...prev, wordLabel]);
                GridService.hintUsed(gridTplId, { word: wordLabel })
                    .then((ro) => {
                        const hu = hintUsedStringFromPlayRo(ro);
                        if (hu !== undefined) {
                            setPuzzleData((p) => mergePuzzleHintUsedIntoState(p, hu));
                        }
                        setGridHintUsedPendingWords((prev) => {
                            const i = prev.indexOf(wordLabel);
                            if (i === -1) return prev;
                            return [...prev.slice(0, i), ...prev.slice(i + 1)];
                        });
                    })
                    .catch((err) => {
                        setGridHintUsedPendingWords((prev) => {
                            const i = prev.indexOf(wordLabel);
                            if (i === -1) return prev;
                            return [...prev.slice(0, i), ...prev.slice(i + 1)];
                        });
                        Logger.error("Error recording grid letter hint use:", err);
                    });
            }

            trackEvent("word_puzzle_letter_hint_consumed", {
                puzzle_type: puzzleType === "HIDDEN_MESSAGE" ? "hidden_message" : "word_search",
                puzzle_id: gridTplId,
                coins_consumed: spend?.coinsSpent ?? 1,
            });
        } catch (err) {
            const message = err?.message || "";
            Logger.warn("Grid letter hint coin spend failed:", message);
            if (/insufficient/i.test(message)) {
                setSnackbarMessage(t("puzzle.playGrid.letter-hint-insufficient", "Not enough coins."));
                setSnackbarSeverity("warning");
                setSnackbarOpen(true);
                setSubscriptionModalOpen(true);
            } else {
                setSnackbarMessage(t("puzzle.playGrid.letter-hint-failed"));
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } finally {
            setLetterHintBusy(false);
        }
    }, [
        enablePaidLetterHint,
        gridListWordsCompletion.isComplete,
        letterHintBusy,
        isAuthenticated,
        hintOrderedWords,
        wordsFound,
        clickedChips,
        hintedWordKeys,
        puzzleData?.filledCoordinates,
        userInfo?.cloudUser?.id,
        gridTplId,
        puzzleType,
        loadWalletBalances,
        t,
    ]);

    // Update parent header: puzzle title primary, story subtitle when both differ (story grid / story UX routes).
    useEffect(() => {
        if (!onPuzzleTitleChange || !puzzleData) return;
        if (!useStoryPlayGridUx) {
            onPuzzleTitleChange(gridPageDisplayTitle || puzzleData.title);
            return;
        }
        const src = gridStorySourceRecord(puzzleData);
        let storyT = "";
        if (src) {
            const { title } = getLocalizedStoryFromTemplate(src);
            storyT = typeof title === "string" ? title.trim() : "";
        }
        const puzzleT = (puzzleData.title || "").trim();
        if (storyT && puzzleT && storyT !== puzzleT) {
            onPuzzleTitleChange(puzzleT);
        } else {
            onPuzzleTitleChange(puzzleT || storyT || gridPageDisplayTitle || puzzleData.title);
        }
    }, [puzzleData, gridPageDisplayTitle, useStoryPlayGridUx, onPuzzleTitleChange]);

    useEffect(() => {
        if (!onPuzzleSubtitleChange || !puzzleData) return;
        if (!useStoryPlayGridUx) {
            onPuzzleSubtitleChange(null);
            return;
        }
        /* Flat hidden-message / word-search play: omit story title in the top bar (story stays in accordion / body). */
        if (puzzleType === "HIDDEN_MESSAGE" || puzzleType === "WORD_SEARCH") {
            onPuzzleSubtitleChange(null);
            return;
        }
        const src = gridStorySourceRecord(puzzleData);
        let storyT = "";
        if (src) {
            const { title } = getLocalizedStoryFromTemplate(src);
            storyT = typeof title === "string" ? title.trim() : "";
        }
        const puzzleT = (puzzleData.title || "").trim();
        if (storyT && puzzleT && storyT !== puzzleT) {
            onPuzzleSubtitleChange(storyT);
        } else {
            onPuzzleSubtitleChange(null);
        }
    }, [puzzleData, useStoryPlayGridUx, onPuzzleSubtitleChange, puzzleType]);

    useEffect(() => {
        if (onFoundWordsChange) {
            onFoundWordsChange(wordsFound);
        }
    }, [wordsFound, onFoundWordsChange]);

    useEffect(() => {
        if (onPuzzleTypeChange) {
            onPuzzleTypeChange(puzzleType);
        }
    }, [puzzleType, onPuzzleTypeChange]);

    // Cleanup: Reset cursor if component unmounts during download
    useEffect(() => {
        return () => {
            if (isPdfDownloading) {
                document.body.style.cursor = 'default';
            }
        };
    }, [isPdfDownloading]);

    useEffect(() => {
        if (puzzleData && puzzleType === "HIDDEN_MESSAGE" && puzzleData.hiddenMessage) {
            const progress =
                gridListWordsCompletion.total > 0
                    ? calculateProgress(gridListWordsCompletion.solvedCount, gridListWordsCompletion.total)
                    : 0;
            setHiddenMessageProgress(progress);

            if (gridListWordsCompletion.isComplete) {
                setHiddenMessage(puzzleData.hiddenMessage);
            }
            setHiddenMessageLength(puzzleData.hiddenMessage?.length ?? 0);
        }
    }, [wordsFound, puzzleType, puzzleData, puzzleData?.hiddenMessage, gridListWordsCompletion]);

    // Confetti + SuccessCard when the player completes the word list (skip restored-already-complete baseline).
    useEffect(() => {
        if (!playHistoryLoaded || !puzzleData || isHowtoOfflineSample) return;

        const total = gridListWordsCompletion.total;
        const solved = gridListWordsCompletion.solvedCount;

        if (!completionBaselineDoneRef.current) {
            completionBaselineDoneRef.current = true;
            prevSolvedCountRef.current = solved;
            return;
        }

        const becameComplete = total > 0 && solved === total && prevSolvedCountRef.current < total;
        prevSolvedCountRef.current = solved;

        if (!becameComplete || successCardShownRef.current) return;

        successCardShownRef.current = true;

        if (!confettiTriggered) {
            triggerConfetti(10 * 1000);
            setConfettiTriggered(true);
        }

        const startedAt = playSessionStartMsRef.current ?? Date.now();
        const elapsedMs = Date.now() - startedAt;
        const attempts = Math.max(puzzleAttemptsRef.current, 1);
        const accuracy = computeSolveAccuracyPercent(total, attempts);
        setSuccessCardStats({
            time: formatSolveDurationMs(elapsedMs),
            accuracy,
        });
        setSuccessRating(0);
        setSuccessCardOpen(true);
    }, [
        playHistoryLoaded,
        puzzleData,
        isHowtoOfflineSample,
        gridListWordsCompletion.total,
        gridListWordsCompletion.solvedCount,
        confettiTriggered,
        triggerConfetti,
        soundEnabled,
    ]);

    const bumpWordFindAttempt = () => {
        puzzleAttemptsRef.current += 1;
    };

    const handlePdf = async () => {
        if (!canDownloadPdf) return;
        if (!isPuzzleFree) {
            const authed = await AuthService.isAuthenticated();
            if (!authed) {
                setLoginDialogFeature('pdf');
                setLoginDialogOpen(true);
                return;
            }
        }
        const savedFormat = userInfo?.cloudUser?.details?.pdfFormat
            || (typeof window !== "undefined" ? window.localStorage.getItem(PDF_FORMAT_DEFAULT_KEY) : null);
        if (savedFormat === "letter" || savedFormat === "a4") {
            await handlePdfFormatConfirm(savedFormat);
        } else {
            setPdfFormatDialogOpen(true);
        }
    };

    const handlePdfFormatConfirm = async (format) => {
        const pdfUrl = format === 'a4' ? puzzleData?.pdfA4Url : puzzleData?.pdfLetterUrl;
        const templateId = puzzleData?.templateId ?? puzzleData?.template?.id ?? gridTplId;
        setIsPdfDownloading(true);
        document.body.style.cursor = 'wait';
        try {
            await GridService.recordAndDownloadPdf(templateId, format, {
                pdfUrl: pdfUrl || undefined,
                title: puzzleData?.title || 'puzzle',
                useDisplayClue: getPdfDownloadUseDisplayClue(),
            });
        } catch (error) {
            Logger.error('PDF generation error:', error.message);
            setSnackbarMessage(t("puzzle.playGrid.pdf-error"));
            setSnackbarOpen(true);
        } finally {
            setIsPdfDownloading(false);
            document.body.style.cursor = 'default';
        }
    };


    const handleBackClick = () => {
        // Only trust router state for library (referrer is unreliable on SPA navigations).
        const fromLibrary = location.state?.fromLibrary === true;

        if (fromLibrary) {
            navigate('/account/library', {
                state: {
                    activeTab: location.state?.activeTab ?? 0
                }
            });
            return;
        }

        if (howtoEmbed) {
            try {
                window.scrollTo({ top: 0, behavior: "smooth" });
            } catch (_) {
                window.scrollTo(0, 0);
            }
            return;
        }

        navigate(gridBrowseListPath);
    };

    const handleSignUpClick = async () => {
        try {
            await AuthService.signup();
        } catch (error) {
            Logger.error("Error redirecting to signup:", error);
        }
    };

    const handleClearClickedWords = () => {
        // Only clear current selection/highlighted cells, not found words
        puzzleGridRef.current?.clearSelection();
        
        // Clear typed word and highlights
        setTypedWord('');
        setHighlightCells([]);
        setWordsDrawerStoryHighlightWord(null);
    };

    const handleClickedWords = (word, cells) => {
        bumpWordFindAttempt();
        if (!word || !puzzleData?.words) return;

        const matchingWord = findMatchingWord(word, puzzleData.words);
        if (!matchingWord) return;

        const wordToAdd = normalizeWord(matchingWord);
        if (isWordAlreadyFound(wordToAdd, wordsFound)) return;

        const updatedWordsFound = [...wordsFound, wordToAdd];
        setWordsFound(updatedWordsFound);
        playWordResolvedChime();
        recordGridPlayHintUsedIfApplicable(wordToAdd, wordsFound, matchingWord);

        // Call the progress API
        if (!howtoSamplePuzzle && userInfo?.cloudUser?.id) {
            GridService.progressPuzzle(userInfo.cloudUser.id, gridTplId, updatedWordsFound)
                .catch((error) => {
                    Logger.error('Error updating progress:', error);
                });
        }

        // Mark the selected cells as found in the grid
        puzzleGridRef.current?.markClickedCellsAsFound(cells);
        triggerWordRevealCelebration(matchingWord, cells);
    };

    const handleChipClick = (word) => {
        setWordsDrawerStoryHighlightWord(null);
        setClickedChips(prev => {
            const newSet = new Set(prev);
            newSet.has(word) ? newSet.delete(word) : newSet.add(word);
            return newSet;
        });
    };

    const handleSubscribe = () => {
        setUserSubscription('premium');
        setSubscriptionModalOpen(false);
    };

    const handleCopyLink = () => {
        setSnackbarMessage(t("puzzle.playGrid.link-copied"));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };


    // Handle typed word input - highlights matching characters as user types
    const handleTypedWordChange = (event) => {
        const value = event.target.value;
        setTypedWord(value);

        if (!value || !value.trim()) {
            setHighlightCells([]);
            return;
        }

        const normalizedValue = normalizeWord(value);

        // Find all matching cells for the typed text (partial word matching)
        const cells = findMatchingCellsAsTyped(normalizedValue, puzzleData?.grid);
        setHighlightCells(cells);

        // Auto-detect if typed word matches a puzzle word and add it automatically
        if (!puzzleData?.words) return;

        const matchingWord = findMatchingWord(normalizedValue, puzzleData.words);
        if (!matchingWord) return;

        const wordToAdd = normalizeWord(matchingWord);
        if (isWordAlreadyFound(wordToAdd, wordsFound)) return;

        // Validate word path
        const validation = validateWordPath(
            normalizedValue,
            puzzleData.grid,
            puzzleData.filledCoordinates
        );

        if (!validation.isValid || !validation.cells) return;

        bumpWordFindAttempt();
        const updatedWordsFound = [...wordsFound, wordToAdd];
        setWordsFound(updatedWordsFound);
        playWordResolvedChime();
        recordGridPlayHintUsedIfApplicable(wordToAdd, wordsFound, matchingWord);

        // Mark cells and update progress
        puzzleGridRef.current?.markClickedCellsAsFound(validation.cells);
        triggerWordRevealCelebration(matchingWord, validation.cells);

        if (!howtoSamplePuzzle && userInfo?.cloudUser?.id) {
            GridService.progressPuzzle(userInfo.cloudUser.id, gridTplId, updatedWordsFound)
                .catch((error) => {
                    Logger.error('Error updating progress:', error);
                });
        }

        // Clear the input and highlights
        setTypedWord('');
        setHighlightCells([]);

        setSnackbarMessage(t("puzzle.playGrid.found", { word: matchingWord }));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    // Handle Enter key to submit typed word
    const handleTypedWordKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmitTypedWord();
        }
    };

    // Submit typed word if it matches
    const handleSubmitTypedWord = () => {
        if (!typedWord?.trim() || !puzzleData?.words) return;

        bumpWordFindAttempt();
        const normalizedTypedWord = normalizeWord(typedWord);
        const matchingWord = findMatchingWord(normalizedTypedWord, puzzleData.words);

        if (!matchingWord) {
            setSnackbarMessage(t("puzzle.playGrid.word-not-in-list"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        const wordToAdd = normalizeWord(matchingWord);

        // Check if word is already found
        if (isWordAlreadyFound(wordToAdd, wordsFound)) {
            setSnackbarMessage(t("puzzle.playGrid.word-already-found"));
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
            setTypedWord('');
            setHighlightCells([]);
            return;
        }

        // Validate word path
        const validation = validateWordPath(
            normalizedTypedWord,
            puzzleData.grid,
            puzzleData.filledCoordinates
        );

        if (!validation.isValid || !validation.cells) {
            setSnackbarMessage(t("puzzle.playGrid.word-not-in-grid"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        const updatedWordsFound = [...wordsFound, wordToAdd];
        setWordsFound(updatedWordsFound);
        playWordResolvedChime();
        recordGridPlayHintUsedIfApplicable(wordToAdd, wordsFound, matchingWord);

        // Mark cells as found in the grid
        puzzleGridRef.current?.markClickedCellsAsFound(validation.cells);
        triggerWordRevealCelebration(matchingWord, validation.cells);

        // Call the progress API
        if (!howtoSamplePuzzle && userInfo?.cloudUser?.id) {
            GridService.progressPuzzle(userInfo.cloudUser.id, gridTplId, updatedWordsFound)
                .catch((error) => {
                    Logger.error('Error updating progress:', error);
                });
        }

        // Clear the input
        setTypedWord('');
        setHighlightCells([]);

        setSnackbarMessage(t("puzzle.playGrid.found", { word: matchingWord }));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };


    if (loading) {
        const loadingBody = (
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    minHeight: playMinHeight,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isHowtoEmbedded ? "transparent" : undefined,
                }}
            >
                <Typography variant="h6" sx={statusTextSx}>
                    {t("puzzle.playGrid.loading")}
                </Typography>
            </Box>
        );
        if (isHowtoEmbedded) return loadingBody;
        return (
            <MistPlayShell sx={playShellSx}>{loadingBody}</MistPlayShell>
        );
    }

    if (!puzzleData) {
        const notFoundBody = (
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    minHeight: playMinHeight,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isHowtoEmbedded ? "transparent" : undefined,
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <Typography variant="h6" sx={statusTextSx}>
                    {t("puzzle.playGrid.not-found")}
                </Typography>
                <Typography variant="body2" sx={statusTextSx}>
                    {t("puzzle.playGrid.not-found-desc", { id: gridTplId })}
                </Typography>
                <Typography variant="body2" sx={statusTextSx}>
                    {t("puzzle.playGrid.not-found-hint")}
                </Typography>
            </Box>
        );
        if (isHowtoEmbedded) return notFoundBody;
        return (
            <MistPlayShell sx={playShellSx}>{notFoundBody}</MistPlayShell>
        );
    }

    // Paid puzzle: wait for user check, then block if no access
    if (showAccessCheck && !settingsLoaded) {
        const checkingBody = (
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    minHeight: playMinHeight,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isHowtoEmbedded ? "transparent" : undefined,
                }}
            >
                <Typography variant="body1" sx={statusTextSx}>
                    {t("puzzle.playGrid.checking-access")}
                </Typography>
            </Box>
        );
        if (isHowtoEmbedded) return checkingBody;
        return (
            <MistPlayShell sx={playShellSx}>{checkingBody}</MistPlayShell>
        );
    }
    if (showAccessCheck && settingsLoaded && !canAccessPuzzle) {
        const gateBody = (
            <Box sx={{ width: "100%" }}>
                <PuzzleAccessGate
                    isAuthenticated={isAuthenticated}
                    userPayPlan={UtilService.resolveWebPayPlan(userInfo?.cloudUser)}
                    onUpgradeClick={() => setSubscriptionModalOpen(true)}
                    puzzleTitle={gridPageDisplayTitle || puzzleData?.title}
                    onBackClick={() => navigate(gridBrowseListPath)}
                />
                <SubscriptionDialog
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onSubscribe={handleSubscribe}
                    triggerReason={subscriptionTriggerReason}
                    userSubscription={userSubscription}
                />
            </Box>
        );
        if (isHowtoEmbedded) return gateBody;
        return <MistPlayShell>{gateBody}</MistPlayShell>;
    }

    if (
        needsPremiumCreditCharge
        && (premiumCreditStatus === "insufficient" || premiumCreditStatus === "error")
    ) {
        const creditGateBody = (
            <Box sx={{ width: "100%", p: { xs: 2, sm: 3 } }}>
                <Alert severity={premiumCreditStatus === "insufficient" ? "warning" : "error"} sx={{ mb: 2 }}>
                    {premiumCreditStatus === "insufficient"
                        ? t("core.puzzleAccess.insufficientCredits")
                        : (premiumCreditError || t("core.puzzleAccess.creditChargeError"))}
                </Alert>
                {premiumCreditStatus === "insufficient" && (
                    <Typography
                        variant="body2"
                        sx={{ mb: 2, textAlign: "center", ...statusTextSx }}
                    >
                        {t("core.puzzleAccess.insufficientCreditsHint")}
                    </Typography>
                )}
                <PuzzleAccessGate
                    isAuthenticated={isAuthenticated}
                    userPayPlan={UtilService.resolveWebPayPlan(userInfo?.cloudUser)}
                    onUpgradeClick={() => setSubscriptionModalOpen(true)}
                    puzzleTitle={gridPageDisplayTitle || puzzleData?.title}
                    onBackClick={() => navigate(gridBrowseListPath)}
                />
                <SubscriptionDialog
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onSubscribe={handleSubscribe}
                    triggerReason={subscriptionTriggerReason}
                    userSubscription={userSubscription}
                />
            </Box>
        );
        if (isHowtoEmbedded) return creditGateBody;
        return <MistPlayShell>{creditGateBody}</MistPlayShell>;
    }

    if (needsPremiumCreditCharge && premiumCreditStatus !== "ok") {
        const chargingBody = (
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    minHeight: playMinHeight,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isHowtoEmbedded ? "transparent" : undefined,
                }}
            >
                <Typography variant="body1" sx={statusTextSx}>
                    {t("puzzle.playGrid.checking-access")}
                </Typography>
            </Box>
        );
        if (isHowtoEmbedded) return chargingBody;
        return (
            <MistPlayShell sx={playShellSx}>{chargingBody}</MistPlayShell>
        );
    }

    const playBody = (
        <Box sx={{
            minHeight: playMinHeight,
            backgroundColor: isHowtoEmbedded ? 'transparent' : 'transparent',
            display: 'flex',
            flexDirection: 'column',
            flex: playBodyFlex,
        }}>
            {/* Main Content — 5:7 when words panel open; full-width grid when closed */}
            <Box sx={{
                ...(isHowtoEmbedded || hasSiteAdBanner
                    ? {
                          flex: '0 0 auto',
                          alignItems: 'flex-start',
                          overflow: 'visible',
                      }
                    : isMobile
                      ? { flex: 1, minHeight: 0, overflow: 'hidden' }
                      : {
                            flex: '0 0 auto',
                            alignItems: shouldShowWordsDrawer ? 'flex-start' : 'stretch',
                            overflow: 'visible',
                        }),
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                py: isHowtoEmbedded ? 0 : isMobile ? 1 : 2,
                px: isHowtoEmbedded ? 0 : isMobile ? 1 : 2,
                backgroundColor: 'transparent',
                gap: isMobile ? 2 : 2
            }}>
                {/* Puzzle Grid Area — 5:7 with story/words panel when drawer open.
                    Fullscreen portals to document.body so the play toolbar is not under MinimalHeader. */}
                {isFullscreen ? (
                    <Box
                        aria-hidden
                        sx={{
                            flex: isMobile ? 1 : 1,
                            minWidth: 0,
                            minHeight: { xs: 280, md: 360 },
                        }}
                    />
                ) : null}
                <FullscreenPortal active={isFullscreen}>
                <Box sx={{
                    flex: isMobile
                        ? (hasSiteAdBanner ? '0 0 auto' : 1)
                        : (shouldShowWordsDrawer ? '5 5 0%' : 1),
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: isFullscreen ? 'hidden' : 'visible',
                    position: isFullscreen ? 'fixed' : 'relative',
                    ...(isFullscreen
                        ? {
                              inset: 0,
                              // Above MinimalHeader (~1100) and typical MUI drawers
                              zIndex: 2000,
                              width: '100vw',
                              height: '100dvh',
                              maxWidth: '100vw',
                              maxHeight: '100dvh',
                              borderRadius: 0,
                              margin: 0,
                              paddingTop: 'env(safe-area-inset-top, 0px)',
                              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                              paddingLeft: 'env(safe-area-inset-left, 0px)',
                              paddingRight: 'env(safe-area-inset-right, 0px)',
                              boxSizing: 'border-box',
                              bgcolor: SITE_BG,
                              boxShadow: 'none',
                              border: 'none',
                              backdropFilter: 'none',
                          }
                        : {
                              minWidth: 0,
                              minHeight: 0,
                              borderRadius: 2,
                              width: '100%',
                              ...(isHowtoEmbedded
                                  ? {
                                        backgroundColor: '#fff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }
                                  : FROSTED_PANEL_SX),
                          }),
                }}>
                    {/* Top Action Bar — always kept above the grid in fullscreen */}
                    <Box sx={{
                        pt: (theme) =>
                            `calc(${theme.spacing(isMobile ? 0.25 : 0.5)} + 2px)`,
                        px: isMobile ? 1 : 2,
                        pb: isMobile ? 0.25 : 0.5,
                        flexShrink: 0,
                        flexGrow: 0,
                        overflow: 'visible',
                        position: 'relative',
                        zIndex: 3,
                        ...(isFullscreen
                            ? {
                                  bgcolor: SURFACE.chrome,
                                  borderBottom: `1px solid ${SURFACE.border}`,
                                  boxShadow: '0 2px 10px rgba(40, 70, 55, 0.12)',
                              }
                            : {
                                  overflowX: isMobile ? 'visible' : 'visible',
                                  overflowY: 'hidden',
                              }),
                    }}>
                        {/* Toolbar: back | spacer | timer+pause + share + actions */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            px: isMobile ? 0.5 : 0,
                            gap: 0.5,
                        }}>
                            {/* Back Button */}
                            <Tooltip title={t("puzzle.playGrid.go-back")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleBackClick}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>

                            {settingsLoaded ? (
                                <Box
                                    ref={bgmButtonWrapRef}
                                    aria-hidden
                                    sx={{
                                        position: "fixed",
                                        left: -9999,
                                        top: 0,
                                        width: 1,
                                        height: 1,
                                        overflow: "hidden",
                                        opacity: 0,
                                        pointerEvents: "none",
                                    }}
                                >
                                    <AudioButton
                                        key={`bgm-${bgmAutoPlay}`}
                                        tracks={PUZZLE_BGM_TRACKS}
                                        defaultPlaying={bgmAutoPlay}
                                        cloudUserId={userInfo?.cloudUser?.id}
                                        leadingDivider={false}
                                        onPlayingChange={setBgmPlaying}
                                    />
                                </Box>
                            ) : null}

                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 1,
                                }}
                            >
                                {letterHintConsumedVisible ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            flexShrink: 1,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                            bgcolor: "#0B3D2E",
                                            color: "#E7FFF4",
                                            border: "1px solid #2E7D57",
                                            boxShadow: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: "100%",
                                        }}
                                    >
                                        {t("puzzle.playGrid.letter-hint-consumed")}
                                    </Box>
                                ) : null}
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexShrink: 0,
                                    flexWrap: isMobile ? "wrap" : "nowrap",
                                }}
                            >
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
                                    <PlayElapsedTimer
                                        running={timerRunning}
                                        ariaLabel={t("puzzle.play-elapsed-time", "Elapsed time")}
                                    />
                                    {!gridListWordsCompletion.isComplete ? (
                                        <Tooltip
                                            title={
                                                timerPaused || visibilityPaused
                                                    ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                    : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                            }
                                            arrow
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (visibilityPaused) {
                                                        continuePlaying();
                                                        setTimerPaused(false);
                                                        return;
                                                    }
                                                    setTimerPaused((p) => !p);
                                                }}
                                                sx={{
                                                    ...playPillButtonSx,
                                                    flexShrink: 0,
                                                    color: ACCENT.light,
                                                    ml: -0.75,
                                                    borderRadius: "50%",
                                                    ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                                }}
                                                aria-label={
                                                    timerPaused || visibilityPaused
                                                        ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                        : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                                }
                                                aria-pressed={timerPaused || visibilityPaused}
                                            >
                                                {timerPaused || visibilityPaused ? (
                                                    <PlayArrowIcon fontSize="small" />
                                                ) : (
                                                    <PauseIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Box>

                                <SocialShareDropdown
                                    compact
                                    puzzleTitle={gridPageDisplayTitle || puzzleData?.title || 'Puzzle'}
                                    puzzleId={gridTplId}
                                    puzzleType={puzzleType}
                                    onCopyLink={handleCopyLink}
                                    imageUrl={gridPreviewImage}
                                />

                            <Tooltip title={t("puzzle.playGrid.download-pdf", "Download PDF")} arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            void handlePdf();
                                        }}
                                        disabled={isPdfDownloading || !canDownloadPdf}
                                        sx={{
                                            ...playPillButtonSx,
                                            ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            borderRadius: "50%",
                                            flexShrink: 0,
                                        }}
                                        aria-label={t("puzzle.playGrid.download-pdf", "Download PDF")}
                                    >
                                        {isPdfDownloading ? (
                                            <CircularProgress size={16} thickness={5} sx={{ color: ACCENT.light }} />
                                        ) : (
                                            <PictureAsPdfIcon />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>

                            {/* Clear Selection Button */}
                            <Tooltip title={t("puzzle.playGrid.clear-selection")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleClearClickedWords}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                    }}
                                >
                                    <RestartAltIcon />
                                </IconButton>
                            </Tooltip>

                            {/* Paid 2-letter hint — Hidden Message & Word Search */}
                            {enablePaidLetterHint && (
                                <Tooltip title={t("puzzle.playGrid.letter-hint")} arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                void handlePaidLetterHintClick();
                                            }}
                                            disabled={gridListWordsCompletion.isComplete || letterHintBusy}
                                            sx={{
                                                ...playPillButtonSx,
                                                ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                                ...(hintButtonGlowing
                                                    ? { backgroundColor: "#FFF8E1" }
                                                    : null),
                                                borderRadius: "50%",
                                                color: hintButtonGlowing ? "#F9A825" : ACCENT.light,
                                                flexShrink: 0,
                                                ...(hintButtonGlowing
                                                    ? {
                                                          animation: `${hintButtonLightOn} 1.4s ease-in-out 2`,
                                                          "&:hover": { backgroundColor: "#FFECB3" },
                                                      }
                                                    : null),
                                                "&.Mui-disabled": {
                                                    opacity: 0.55,
                                                    animation: "none",
                                                },
                                            }}
                                            aria-label={t("puzzle.playGrid.letter-hint")}
                                            aria-busy={letterHintBusy}
                                        >
                                            {letterHintBusy ? (
                                                <CircularProgress size={16} thickness={5} sx={{ color: ACCENT.light }} />
                                            ) : hintButtonGlowing ? (
                                                <LightbulbIcon />
                                            ) : (
                                                <LightbulbOutlinedIcon />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}

                            {/* Legacy free first-letter toggle — currently unused for HM/WS */}
                            {enableGridLetterHintsUi && (
                                <Tooltip title={gridHintButtonTooltip} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            if (hintsEnabled) {
                                                setHintsEnabled(false);
                                                return;
                                            }
                                            if (gridHintSlotBudget <= 0) {
                                                return;
                                            }
                                            if (!isPuzzleFree) {
                                                const authed = await AuthService.isAuthenticated();
                                                const hasStandardPlan = UtilService.isStandardPayPlanForUser(userInfo?.cloudUser);
                                                if (!authed || !hasStandardPlan) {
                                                    setLoginDialogFeature('hint');
                                                    setLoginDialogOpen(true);
                                                    return;
                                                }
                                            }

                                            setHintsEnabled(true);
                                        }}
                                        sx={{
                                            ...playPillButtonSx,
                                            ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            ...(hintsEnabled
                                                ? {
                                                      backgroundColor: purple[100],
                                                      color: '#000',
                                                      '&:hover': { backgroundColor: purple[200] },
                                                  }
                                                : null),
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            opacity: !hintsEnabled && gridHintSlotBudget <= 0 ? 0.5 : 1,
                                        }}
                                    >
                                        <LightbulbIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title={t("nav.settings", "Settings")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        ...(settingsAnchor
                                            ? {
                                                  backgroundColor: "#3BA666",
                                                  color: "#fff",
                                                  "&:hover": { backgroundColor: "#2E8B57" },
                                              }
                                            : null),
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                    }}
                                    aria-label={t("nav.settings", "Settings")}
                                    aria-haspopup="true"
                                    aria-expanded={Boolean(settingsAnchor)}
                                >
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>

                            {/* Fullscreen Toggle Button */}
                            <Tooltip title={isFullscreen ? t("puzzle.playGrid.exit-fullscreen") : t("puzzle.playGrid.show-fullscreen")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setSettingsAnchor(null);
                                        setIsFullscreen((v) => !v);
                                    }}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        ...(isFullscreen
                                            ? {
                                                  backgroundColor: '#3BA666',
                                                  boxShadow:
                                                      '0 0 0 3px rgba(59, 166, 102, 0.35), 0 2px 8px rgba(0,0,0,0.12)',
                                                  color: '#fff',
                                                  '&:hover': { backgroundColor: '#2E8B57' },
                                              }
                                            : null),
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                    }}
                                    aria-label={
                                        isFullscreen
                                            ? t("puzzle.playGrid.exit-fullscreen")
                                            : t("puzzle.playGrid.show-fullscreen")
                                    }
                                >
                                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                            </Tooltip>

                            {/* Words List Toggle Button */}
                            <Tooltip title={t("puzzle.playGrid.toggle-words-list")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setWordsDrawerOpen((open) => {
                                            if (open) setWordsDrawerAiMode(false);
                                            return !open;
                                        });
                                    }}
                                    sx={{
                                        ...playPillButtonSx,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                        ...(wordsDrawerOpen
                                            ? {
                                                  backgroundColor: '#3BA666',
                                                  color: '#fff',
                                                  '&:hover': { backgroundColor: '#2E8B57' },
                                              }
                                            : null),
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <LibraryBooksIcon />
                                </IconButton>
                            </Tooltip>
                            </Box>
                        </Box>
                        <Popover
                            open={Boolean(settingsAnchor)}
                            anchorEl={settingsAnchor}
                            onClose={() => setSettingsAnchor(null)}
                            disableScrollLock
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        mt: 0.75,
                                        p: 1.5,
                                        minWidth: 220,
                                        borderRadius: 2,
                                        boxShadow: 4,
                                    },
                                },
                            }}
                        >
                            {isAuthenticated ? (
                                <>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                                    >
                                        {t("puzzle.sliding.settings-wallet", "Balance")}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr 1fr",
                                            gap: 0.75,
                                            mb: 1.25,
                                        }}
                                    >
                                        {[
                                            {
                                                key: "play",
                                                label: t("puzzle.sliding.settings-play-credits", "Play"),
                                                value: walletPlayCredits,
                                                icon: <TollIcon sx={{ fontSize: "1.1rem", color: "#2761F5" }} />,
                                            },
                                            {
                                                key: "hints",
                                                label: t("puzzle.sliding.settings-hint-credits", "Hints"),
                                                value: walletHintCredits,
                                                icon: (
                                                    <LightbulbOutlinedIcon
                                                        sx={{ fontSize: "1.1rem", color: ACCENT.light }}
                                                    />
                                                ),
                                            },
                                            {
                                                key: "coins",
                                                label: t("puzzle.sliding.settings-coins", "Coins"),
                                                value: walletPuzzleCoins,
                                                icon: (
                                                    <MonetizationOnIcon
                                                        sx={{ fontSize: "1.1rem", color: "#d97706" }}
                                                    />
                                                ),
                                            },
                                        ].map((item) => (
                                            <Box
                                                key={item.key}
                                                sx={{
                                                    textAlign: "center",
                                                    py: 0.75,
                                                    px: 0.5,
                                                    borderRadius: 1,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    bgcolor: "action.hover",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block", fontWeight: 600, lineHeight: 1.2 }}
                                                >
                                                    {item.label}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 0.35,
                                                        mt: 0.25,
                                                    }}
                                                >
                                                    {item.icon}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 800, lineHeight: 1.2 }}
                                                    >
                                                        {walletBalancesLoading && item.value == null
                                                            ? "…"
                                                            : Number(item.value ?? 0).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                    <Divider sx={{ my: 1.25 }} />
                                </>
                            ) : null}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {isBoldFont
                                            ? t("puzzle.playGrid.switch-light")
                                            : t("puzzle.playGrid.switch-bold")}
                                    </Typography>
                                    <Tooltip
                                        title={
                                            isBoldFont
                                                ? t("puzzle.playGrid.switch-light")
                                                : t("puzzle.playGrid.switch-bold")
                                        }
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => setIsBoldFont((v) => !v)}
                                            sx={{
                                                color: isBoldFont ? ACCENT.light : "text.secondary",
                                                ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            }}
                                            aria-label={
                                                isBoldFont
                                                    ? t("puzzle.playGrid.switch-light")
                                                    : t("puzzle.playGrid.switch-bold")
                                            }
                                            aria-pressed={isBoldFont}
                                        >
                                            <FormatBoldIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {t("puzzle.jigsaw.background-music", "Background music")}
                                    </Typography>
                                    <Tooltip
                                        title={bgmPlaying ? t("audio-button.mute") : t("audio-button.play")}
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const btn = bgmButtonWrapRef.current?.querySelector("button");
                                                    btn?.click();
                                                }}
                                                sx={{
                                                    color: ACCENT.light,
                                                    ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                                }}
                                                aria-label={
                                                    bgmPlaying ? t("audio-button.mute") : t("audio-button.play")
                                                }
                                            >
                                                {bgmPlaying ? (
                                                    <MusicNoteIcon fontSize="small" />
                                                ) : (
                                                    <MusicOffIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {t("puzzle.jigsaw.sound-effects", "Sound effects")}
                                    </Typography>
                                    <Tooltip
                                        title={
                                            soundEnabled
                                                ? t("puzzle.jigsaw.mute-sound-effects", "Mute sound effects")
                                                : t("puzzle.jigsaw.enable-sound-effects", "Enable sound effects")
                                        }
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={handleToggleSoundEffects}
                                            sx={{
                                                color: ACCENT.light,
                                                ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                            }}
                                            aria-label={
                                                soundEnabled
                                                    ? t("puzzle.jigsaw.mute-sound-effects", "Mute sound effects")
                                                    : t(
                                                          "puzzle.jigsaw.enable-sound-effects",
                                                          "Enable sound effects",
                                                      )
                                            }
                                            aria-pressed={!soundEnabled}
                                        >
                                            {soundEnabled ? (
                                                <VolumeUpIcon fontSize="small" />
                                            ) : (
                                                <VolumeOffIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Popover>
                    </Box>

                    <Box sx={{
                        flex: isFullscreen ? '1 1 auto' : '0 0 auto',
                        minHeight: isFullscreen ? 0 : undefined,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: isFullscreen ? 'hidden' : 'visible',
                        pt: 0.5,
                        px: isFullscreen ? { xs: 1, sm: 2 } : 2,
                        alignItems: isFullscreen ? 'center' : 'stretch',
                        justifyContent: isFullscreen ? 'center' : 'flex-start',
                        width: '100%',
                        boxSizing: 'border-box',
                        pb: isFullscreen ? 1.5 : 2,
                        position: 'relative',
                        zIndex: 1,
                        ...(isFullscreen
                            ? {
                                  containerType: 'size',
                              }
                            : null),
                    }}>
                    <Box sx={{
                        ...(isFullscreen
                            ? {
                                  // Fit the remaining area under the toolbar (not full viewport)
                                  width: 'min(100cqw, 100cqh)',
                                  height: 'min(100cqw, 100cqh)',
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  aspectRatio: '1',
                                  // Fallback when container queries are unavailable
                                  '@supports not (width: 1cqw)': {
                                      width: 'min(100%, calc(100dvh - 7rem))',
                                      height: 'auto',
                                      maxHeight: '100%',
                                  },
                              }
                            : {
                                  width: '100%',
                                  maxWidth: '100%',
                                  aspectRatio: '1',
                              }),
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        alignSelf: isFullscreen ? 'center' : 'stretch',
                        minWidth: 0,
                        minHeight: 0,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 1,
                    }}>
                        <GridGrid
                            ref={puzzleGridRef}
                            grid={puzzleData.grid}
                            words={puzzleData.words}
                            wordsFound={wordsFound}
                            onWordFound={(word) => {
                                const cells = findWordInGrid(word, puzzleData.filledCoordinates);
                                handleClickedWords(word, cells || []);
                            }}
                            onClearClickedWords={handleClearClickedWords}
                            onClickedWords={handleClickedWords}
                            gridSize={resolveGridSize(puzzleData)}
                            isFullscreen={isFullscreen}
                            isBoldFont={isBoldFont}
                            filledCoordinates={puzzleData.filledCoordinates}
                            isHintMode={enableGridLetterHintsUi && hintsEnabled}
                            paidHintRevealCells={paidHintRevealCells}
                            highlightCells={highlightCells}
                            hiddenMessageCells={hiddenMessageCells}
                            hintOrderedWords={hintOrderedWords}
                            hintRevealWordLimit={gridHintSlotBudget}
                            cellFontPx={isAdminRole ? adminGridCellFontPx : undefined}
                            mobileMinLetterPx={14}
                            celebrateCellIndexByKey={celebrateCellIndexByKey}
                        />
                    </Box>

                        {!isFullscreen && !shouldShowWordsDrawer ? (
                            <ManualValidationInput
                                manualValidation={true}
                                typedWord={typedWord}
                                onTypedWordChange={handleTypedWordChange}
                                onTypedWordKeyDown={handleTypedWordKeyDown}
                                userRole={userInfo?.cloudUser?.userRole}
                                gridLetterFontPx={isAdminRole ? adminGridCellFontPx : undefined}
                                onGridLetterFontPxChange={
                                    isAdminRole ? setAdminGridCellFontPx : undefined
                                }
                            />
                        ) : null}

                        {!isFullscreen &&
                            !shouldShowWordsDrawer &&
                            (puzzleType === "HIDDEN_MESSAGE" || puzzleType === "WORD_SEARCH") && (
                                <GridPlayProgressAccordions
                                    placement="underGrid"
                                    puzzleType={puzzleType}
                                    t={t}
                                    hiddenMessageLength={hiddenMessageLength}
                                    hiddenMessage={hiddenMessage}
                                    hiddenMessageProgress={hiddenMessageProgress}
                                    pdfSolvedCurrent={pdfSolvedCurrent}
                                    pdfWordsTotal={pdfWordsTotal}
                                    puzzleWordsCount={puzzleData?.words?.length ?? 0}
                                    isWordSearchComplete={isWordSearchComplete}
                                    completionActionsDisabled={completionActionsDisabled}
                                    settingsLoaded={settingsLoaded}
                                    isAuthenticated={isAuthenticated}
                                    setFeedbackDialogOpen={setFeedbackDialogOpen}
                                    handleBackClick={handleBackClick}
                                    handleSignUpClick={handleSignUpClick}
                                />
                            )}
                        {!isFullscreen ? <CopyrightLine /> : null}
                    </Box>
                </Box>
                </FullscreenPortal>

                {/* Words Sidebar */}
                {/* Only show drawer after settings are loaded AND drawer is open */}
                {/* Using computed value to prevent flicker */}
                {shouldShowWordsDrawer && (
                    <Box sx={{
                        flex: isMobile ? 'none' : '7 7 0%',
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: undefined, sm: 420 },
                        minHeight: 0,
                        // Cap sidebar to viewport so long “Words to find” scrolls inside; left column stays grid-height.
                        ...(!isMobile && !isHowtoEmbedded
                            ? {
                                  maxHeight: (theme) =>
                                      `calc(100vh - 5.5rem - ${theme.spacing(4)})`,
                              }
                            : {}),
                        backgroundColor: isHowtoEmbedded ? '#f8f9fa' : undefined,
                        ...(isHowtoEmbedded
                            ? {}
                            : FROSTED_PANEL_SX),
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minWidth: 0,
                            flex: isHowtoEmbedded || hasSiteAdBanner ? '0 0 auto' : 1,
                            minHeight: isHowtoEmbedded || hasSiteAdBanner ? 'auto' : 0,
                            overflow: isHowtoEmbedded || hasSiteAdBanner ? 'visible' : 'hidden',
                        }}>
                            {showNetwork && hasWordNetwork ? (
                                <WordNetworkPanel
                                    wordNetwork={wordNetworkData}
                                    onToggle={() => {
                                        setShowNetwork(false);
                                        setSelectedNetworkWord(null);
                                    }}
                                    onNodeSelect={(word) => setSelectedNetworkWord(word)}
                                    hideVisibilityToggle={true}
                                    hideMostConnected={true}
                                    hideNodeEdgeCount={true}
                                />
                            ) : (
                                <Box sx={{
                                    flex: isHowtoEmbedded || hasSiteAdBanner ? '0 0 auto' : 1,
                                    minHeight: isHowtoEmbedded || hasSiteAdBanner ? 'auto' : 0,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}>
                                <WordsToFindDrawer
                                    open={wordsDrawerOpen}
                                    words={
                                        puzzleType === "HIDDEN_MESSAGE"
                                            ? (puzzleData?.words || []).filter(
                                                  (word) =>
                                                      word.toLowerCase() !==
                                                      puzzleData?.hiddenMessage?.toLowerCase()
                                              )
                                            : puzzleData?.words || []
                                    }
                                    wordsFound={wordsFound}
                                    clickedChips={clickedChips}
                                    hintedWordKeys={hintedWordKeys}
                                    onChipClick={handleChipClick}
                                    onHide={() => {
                                        setWordsDrawerAiMode(false);
                                        setWordsDrawerOpen(false);
                                    }}
                                    assistantMagicVisible={showAssistantMagic}
                                    assistantMagicTooltipTitle={
                                        canUseAssistant
                                            ? t(
                                                  "puzzle.playCross.ai-chat-tooltip",
                                                  "Open Chat with AI in the word list"
                                              )
                                            : t(
                                                  "puzzle.playCross.assistant-magic-sign-in",
                                                  "AI Assistant"
                                              )
                                    }
                                    assistantInputLocked={
                                        wordsDrawerAiMode && !canUseAssistant
                                    }
                                    assistantLockedInputText={t(
                                        "puzzle.playCross.assistant-locked-input",
                                        "Ask Assistant"
                                    )}
                                    onOpenChatWithAi={
                                        showAssistantMagic
                                            ? () => setWordsDrawerAiMode(true)
                                            : undefined
                                    }
                                    chatWithAiOpen={wordsDrawerAiMode}
                                    onCloseChatWithAi={() => setWordsDrawerAiMode(false)}
                                    assistantMessages={assistantMessages}
                                    assistantPrompt={assistantPrompt}
                                    onAssistantPromptChange={setAssistantPrompt}
                                    onAssistantSend={handleAssistantSubmit}
                                    assistantLoading={assistantLoading}
                                    assistantHistoryLoading={assistantHistoryLoading}
                                    assistantCreditsTotal={
                                        userInfo?.cloudUser?.assistantCreditsTotal
                                    }
                                    chatWithAiTitle={t(
                                        "puzzle.playCross.chat-with-ai",
                                        "Chat with AI"
                                    )}
                                    chatWithAiPlaceholder={t(
                                        "puzzle.playCross.assistant-placeholder",
                                        "Ask for a hint about this puzzle"
                                    )}
                                    onNetworkToggle={() => {
                                        setShowNetwork(!showNetwork);
                                        if (!showNetwork) {
                                            setSelectedNetworkWord(null);
                                        }
                                    }}
                                    language={
                                        puzzleData?.language ||
                                        puzzleData?.template?.language ||
                                        "english"
                                    }
                                    showNetwork={showNetwork}
                                    hasWordNetwork={hasWordNetwork}
                                    selectedNetworkWord={selectedNetworkWord}
                                    storyHighlightedWord={
                                        useStoryPlayGridUx ? wordsDrawerStoryHighlightWord : null
                                    }
                                    skipAuthCheck={isPuzzleFree}
                                    displayClue={
                                        puzzleType === "HIDDEN_MESSAGE" ||
                                        puzzleType === "WORD_SEARCH"
                                            ? null
                                            : puzzleData?.displayClue ??
                                              puzzleData?.display_clue ??
                                              puzzleData?.template?.displayClue ??
                                              puzzleData?.template?.display_clue ??
                                              null
                                    }
                                    collapsibleMeaningPanel={
                                        puzzleType === "WORD_SEARCH" ||
                                        puzzleType === "HIDDEN_MESSAGE"
                                    }
                                    onOpenStory={
                                        hasPlayStoryContent
                                            ? () => setStoryDialogOpen(true)
                                            : undefined
                                    }
                                    storySlot={gridStoryAccordion}
                                    bankLockIn={bankLockIn}
                                />
                                </Box>
                            )}
                            {(puzzleType === "HIDDEN_MESSAGE" || puzzleType === "WORD_SEARCH") && (
                                <GridPlayProgressAccordions
                                    placement="sidebar"
                                    puzzleType={puzzleType}
                                    t={t}
                                    hiddenMessageLength={hiddenMessageLength}
                                    hiddenMessage={hiddenMessage}
                                    hiddenMessageProgress={hiddenMessageProgress}
                                    pdfSolvedCurrent={pdfSolvedCurrent}
                                    pdfWordsTotal={pdfWordsTotal}
                                    puzzleWordsCount={puzzleData?.words?.length ?? 0}
                                    isWordSearchComplete={isWordSearchComplete}
                                    completionActionsDisabled={completionActionsDisabled}
                                    settingsLoaded={settingsLoaded}
                                    isAuthenticated={isAuthenticated}
                                    setFeedbackDialogOpen={setFeedbackDialogOpen}
                                    handleBackClick={handleBackClick}
                                    handleSignUpClick={handleSignUpClick}
                                />
                            )}
                            <Box sx={{ width: "100%", flexShrink: 0, minWidth: 0 }}>
                                <ManualValidationInput
                                    manualValidation={true}
                                    typedWord={typedWord}
                                    onTypedWordChange={handleTypedWordChange}
                                    onTypedWordKeyDown={handleTypedWordKeyDown}
                                    userRole={userInfo?.cloudUser?.userRole}
                                    gridLetterFontPx={isAdminRole ? adminGridCellFontPx : undefined}
                                    onGridLetterFontPxChange={
                                        isAdminRole ? setAdminGridCellFontPx : undefined
                                    }
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Snackbar */}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />

            {hasPlayStoryContent ? (
                <StoryPlayStoryDialog
                    open={storyDialogOpen}
                    onClose={() => setStoryDialogOpen(false)}
                    summaryTitle={
                        String(storyPresentation.titleAboveGrid || "").trim()
                            ? String(storyPresentation.titleAboveGrid).trim()
                            : t("puzzle.playCross.story-card-heading")
                    }
                    imageUrl={storyPresentation.imageUrl || undefined}
                    storyShareId={
                        isHowtoOfflineSample ? undefined : storyShareIdFromPayload || undefined
                    }
                    storyShareTitle={storyShareTitleForShare}
                    storySharePuzzleType={puzzleType || "WORD_SEARCH"}
                    storyId={storyPresentation.storyId ?? undefined}
                    showSlidingPuzzle={storyPresentation.showSlidingPuzzle}
                    showJigsawPuzzle={storyPresentation.showJigsawPuzzle}
                >
                    {gridStoryDialogChildren}
                </StoryPlayStoryDialog>
            ) : null}

            {/* Subscription Dialog */}
            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                onSubscribe={handleSubscribe}
                triggerReason={subscriptionTriggerReason}
                userSubscription={userSubscription}
            />

            {/* Login Confirmation Dialog */}
            <LoginConfirmDialog
                open={loginDialogOpen}
                onClose={() => setLoginDialogOpen(false)}
                onConfirm={() => {
                    navigate('/login');
                }}
                title={t("puzzle.playGrid.login-required")}
                message={
                    loginDialogFeature === 'hint'
                        ? t("puzzle.playGrid.login-hint-msg")
                        : t("puzzle.playGrid.login-pdf-msg")
                }
            />

            {/* Welcome-back popup disabled — timer still pauses while tab is hidden
            <GamePausedOverlay
                open={pauseOverlayOpen}
                onContinue={() => {
                    continuePlaying();
                    setTimerPaused(false);
                }}
            />
            */}

            {/* PDF format selection dialog */}
            <PdfFormatDialog
                open={pdfFormatDialogOpen}
                onClose={() => setPdfFormatDialogOpen(false)}
                onConfirm={handlePdfFormatConfirm}
            />

            {/* Feedback Dialog */}
            <FeedbackDialog
                open={feedbackDialogOpen}
                onClose={() => setFeedbackDialogOpen(false)}
                onSubmit={async (feedbackData) => {
                    try {
                        await FeedbackService.submitFeedback({
                            gridId: gridTplId,
                            crossId: null,
                            cloudUserId: userInfo?.cloudUser?.id || null,
                            stars: feedbackData.stars,
                            difficultyDelta: feedbackData.difficultyDelta,
                            comment: feedbackData.comment
                        });
                        setFeedbackSubmitted(true);
                        setSnackbarMessage("Thank you for your feedback!");
                        setSnackbarSeverity("success");
                        setSnackbarOpen(true);
                    } catch (error) {
                        Logger.error("Error submitting feedback:", error);
                        setSnackbarMessage("Failed to submit feedback. Please try again.");
                        setSnackbarSeverity("error");
                        setSnackbarOpen(true);
                    }
                }}
                puzzleType={puzzleType}
            />

            <SuccessCard
                variant="modal"
                open={successCardOpen}
                onClose={() => setSuccessCardOpen(false)}
                stats={successCardStats}
                guest={!isAuthenticated}
                celebrationSoundEnabled={soundEnabled}
                showRating={isAuthenticated}
                ratingValue={successRating}
                ratingBusy={successRatingBusy}
                onRate={async (stars) => {
                    setSuccessRating(stars);
                    setSuccessRatingBusy(true);
                    try {
                        await FeedbackService.submitFeedback({
                            gridId: gridTplId,
                            crossId: null,
                            cloudUserId: userInfo?.cloudUser?.id || null,
                            stars,
                            details: { puzzleType },
                        });
                    } catch (error) {
                        Logger.error("Error submitting grid rating:", error);
                    } finally {
                        setSuccessRatingBusy(false);
                    }
                }}
                onNext={() => {
                    setSuccessCardOpen(false);
                    if (!isAuthenticated) {
                        void handleSignUpClick();
                    } else {
                        navigate(gridBrowseListPath);
                    }
                }}
            />

            {wordRevealFx ? (
                <WordRevealFx
                    fxId={wordRevealFx.fxId}
                    word={wordRevealFx.word}
                    cellRects={wordRevealFx.cellRects}
                    bankRect={wordRevealFx.bankRect}
                    onComplete={handleWordRevealComplete}
                    onBankImpact={handleWordRevealBankImpact}
                />
            ) : null}
        </Box>
    );

    if (isHowtoEmbedded) return playBody;
    return (
        <MistPlayShell sx={playShellSx}>{playBody}</MistPlayShell>
    );
};

export default GridPlayMain;
